let columnMappings = new Map();
let selectedCensusSummaryTab = 'validation'; // Default to summary tab
import { ConfigManager } from './config.js';
import { initializeConnectivityTesting } from './test.js';

/**
 * Shows a toast message for errors
 * @param {string} message - Error message to display
 * @param {number} duration - Duration in milliseconds before auto-dismiss
 */
function showErrorToast(message, duration = 5000) {
    // Remove any existing toasts
    const existingToasts = document.querySelectorAll('.error-toast');
    existingToasts.forEach(toast => toast.remove());
    
    // Create new toast
    const toast = document.createElement('div');
    toast.className = 'error-toast';
    toast.textContent = message;
    
    // Add to DOM
    document.body.appendChild(toast);
    
    // Auto-dismiss
    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(100%)';
        toast.style.transition = 'all 0.3s ease-out';
        
        // Remove from DOM after transition
        setTimeout(() => {
            toast.remove();
        }, 300);
    }, duration);
}

/**
 * Shows an error message in the mapping table section
 * @param {string} message - Error message to display
 * @param {number} duration - Duration in milliseconds before auto-dismiss (0 = no auto-dismiss)
 */
function showMappingError(message, duration = 5000) {
    // Create or get container
    let container = document.querySelector('.mapping-errors-container');
    if (!container) {
        container = document.createElement('div');
        container.className = 'mapping-errors-container';
        
        // Find mapping table to insert before
        const mappingTable = document.querySelector('.mapping-table');
        if (mappingTable) {
            mappingTable.parentNode.insertBefore(container, mappingTable);
        } else {
            // Fallback - add to tab content
            const tabContent = document.getElementById('mapping-overrides-tab');
            if (tabContent) {
                tabContent.appendChild(container);
            }
        }
    }
    
    // Create error message
    const errorMsg = document.createElement('div');
    errorMsg.className = 'error-message visible';
    errorMsg.textContent = message;
    
    // Add close button
    const closeBtn = document.createElement('span');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.float = 'right';
    closeBtn.style.cursor = 'pointer';
    closeBtn.style.fontWeight = 'bold';
    closeBtn.onclick = () => errorMsg.remove();
    errorMsg.appendChild(closeBtn);
    
    // Add to container
    container.appendChild(errorMsg);
    
    // Auto-dismiss if duration > 0
    if (duration > 0) {
        setTimeout(() => {
            errorMsg.style.opacity = '0';
            errorMsg.style.transition = 'opacity 0.3s ease';
            
            // Remove from DOM after transition
            setTimeout(() => {
                errorMsg.remove();
            }, 300);
        }, duration);
    }
    
    return errorMsg;
}

// Modify the processCensusData function to clean headers before API submission
async function processCensusData() {
    try {
        // Clear previous errors
        document.getElementById("products-error").style.display = "none";
        document.getElementById("census-type-error").style.display = "none";

        // Get selected products
        const productSelect = document.getElementById("products-select");
        const selectedProducts = Array.from(productSelect.selectedOptions).map(option => option.value);

        // Get selected census type
        const selectedCensusType = document.querySelector('input[name="censusType"]:checked')?.value;

        // Validate selections
        let hasError = false;

        if (selectedProducts.length === 0) {
            const errorElement = document.getElementById("products-error");
            errorElement.textContent = "Please select at least one product";
            errorElement.style.display = "block";
            hasError = true;
        }

        if (!selectedCensusType) {
            const errorElement = document.getElementById("census-type-error");
            errorElement.textContent = "Please select a census type";
            errorElement.style.display = "block";
            hasError = true;
        }

        if (hasError) {
           return;
        }

        // Log selections
        logMessage("Selected products: " + selectedProducts.join(", "));
        logMessage("Census type: " + selectedCensusType);

        // Start processing
        logMessage("Starting to process census data...");
        document.getElementById("loading-indicator").style.display = "block";
        document.getElementById("check-census").disabled = true;
        logMessage("Current Mappings...");
        logMessage(Array.from(columnMappings.entries()).map(([key, value]) => `${key}: ${value}`).join(', '));

        await Excel.run(async (context) => {
            // Get workbook name
            const workbook = context.workbook;
            workbook.load("name");
            await context.sync();
            
            logMessage(`Workbook name loaded: ${workbook.name}`);
            
            // Get all worksheets
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");  // Make sure to load sheet names
            await context.sync();

            if (worksheets.items.length === 0) {
                throw new Error("No worksheets found in workbook");
            }

            logMessage(`Loaded ${worksheets.items.length} worksheets.`);

            // Find Census sheet
            const sheet = worksheets.items.find(sheet => sheet.name.toLowerCase() === 'census');
            if (!sheet) {
                const errorDisplay = document.getElementById("error-display");
                errorDisplay.style.display = "block";
                errorDisplay.innerHTML = `<p>Error: Please ensure there is a sheet named 'Census' containing source data</p>`;
                return;
            }

            logMessage(`Selected sheet: ${sheet.name}`);
            const range = sheet.getUsedRange();
     
            // Get settings
            const inferenceType = document.querySelector('input[name="inferenceType"]:checked')?.value || "basic";
            const confidenceScore = inferenceType === "advanced" 
                 ? document.getElementById("confidence-score")?.value 
                 : null;
        
            const enable_ai = inferenceType == "advanced";
            logMessage(`Settings: Inference type - ${inferenceType}, Confidence score - ${confidenceScore}, AI enable status - ${enable_ai}`);
            
            // Initialize data object with products and census type
            const cacheEnabled = document.getElementById("enable-caching")?.checked;
            let allSheetsData = {
                filename: workbook.name,
                sheets: {},
                products: selectedProducts,
                censusType: selectedCensusType,
                enable_ai: enable_ai,
                confidenceScore: confidenceScore,
                columnMappings: Object.fromEntries(columnMappings),
                cacheEnabled: cacheEnabled
            };

            const errorDisplay = document.getElementById("error-display");
            errorDisplay.style.display = "none";
            const actionsPanel = document.getElementById('actions-panel');
            actionsPanel.style.display = "none";
            
            range.load(["values", "rowCount", "columnCount"]);
            await context.sync();

            if (!range.values || range.values.length === 0) {
                throw new Error(`Selected sheet is empty: ${sheet.name}`);
            }
            if (checkDuplicateHeaders(range.values[0])) {
                return;
            }        

            logMessage(`Sheet ${sheet.name} has data.`);
            // Convert to JSON
            const originalHeaders = range.values[0];
            // Clean the headers
            const headers = originalHeaders.map(header => cleanHeaderText(header));
            const data = [];

            const rowsToProcess = range.values.length;

            for (let i = 1; i < rowsToProcess; i++) {
                const row = {};
                row["_rowNumber"] = i + 1;
                
                let isEmptyRow = true;
                for (let j = 0; j < headers.length; j++) {
                    if (range.values[i][j] !== undefined && range.values[i][j] !== '') {
                        row[headers[j]] = range.values[i][j];
                        isEmptyRow = false;
                    }
                }
                if (!isEmptyRow) {
                    data.push(row);
                }
            }
                       
            // Add sheet data to the collection
            allSheetsData.sheets[sheet.name] = {
                headers: headers,
                data: data,
                totalRows: range.rowCount - 1
            };

            logMessage(`Processed sheet ${sheet.name}:`, {
                headerCount: headers.length,
                rowCount: data.length,
                totalRowsInSheet: range.rowCount - 1,
                sampleRow: data[0]
            });

            logMessage("Calling API:", allSheetsData.filename);
            //logMessage(JSON.stringify(allSheetsData))

            const startTime = new Date().getTime();
            const apiUrl = await ConfigManager.getApiUrl('/process-census');
            logMessage("Calling API for process-census: "+apiUrl);
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                body: JSON.stringify(allSheetsData)
            });

            const endTime = new Date().getTime();
            logMessage(`Request took ${endTime - startTime} milliseconds`);                    
            if (!response.ok) {
                throw new Error(`https error! status: ${response.status}`);
            }

            const result = await response.json();
            try{
                logMessage("API Response received: " + JSON.stringify(result));
            }catch(e){
                logMessage("Response display error:", e.toString());}

            if (result.status === 'success' && result.change_summary !== null) {
                const actionsPanel = document.getElementById('actions-panel');
                actionsPanel.innerHTML = '';
                actionsPanel.appendChild(createResponsePanel(result));
                actionsPanel.style.display = 'block';
            } else if(result.status === 'success' && result.change_summary == null) {
                document.getElementById("check-census").disabled = false;
            } else {
                throw new Error('Invalid API response structure');
            }
        });

    } catch (error) {
        logMessage("ProcessCensusData error:", error.toString());
        console.error("ProcessCensusData error:", error);
        const errorDisplay = document.getElementById("error-display");
        errorDisplay.style.display = "block";
        errorDisplay.innerHTML = `<p>Error: ${error.message}</p>`;
    } finally {
        logMessage("Processing complete.");
        document.getElementById("loading-indicator").style.display = "none";
        document.getElementById("check-census").disabled = false;
    }
}

Office.onReady((info) => {
    if (info.host === Office.HostType.Excel) {
        // Replace the existing check-census onclick handler with our async version
        document.getElementById("check-census").onclick = () => {
            processAsyncCensus({
                // Optional custom callbacks for progress updates
                onProgress: (progress) => {
                    // Update a progress bar or other UI element if desired
                    logMessage(`Processing progress: ${progress}%`);
                    
                    // Optional: Update a visual progress indicator
                    const progressIndicator = document.querySelector('.progress-value');
                    if (progressIndicator) {
                        progressIndicator.style.width = `${progress}%`;
                        progressIndicator.setAttribute('aria-valuenow', progress);
                    }
                },
                onStatusChange: (status, step) => {
                    logMessage(`Status: ${status}, Step: ${step}`);
                    
                    // Optional: Update status indicator in the UI
                    const statusIndicator = document.querySelector('.status-message');
                    if (statusIndicator) {
                        statusIndicator.textContent = step || status;
                    }
                }
            });
        };
        
        // Initialize products
        initializeProducts();
        
        // Initialize connectivity testing
        initializeConnectivityTesting();

        // Add tab switching initialization
        document.querySelectorAll('.tab-button').forEach(button => {
            button.addEventListener('click', () => switchTab(button.dataset.tab));
        });

        // Initialize mapping overrides if it's the active tab
        if (document.querySelector('.tab-button[data-tab="mapping-overrides"]').classList.contains('active')) {
            initializeMappingOverrides();
        }

        // Initialize class mapping functionality
        document.getElementById('class-column-select')?.addEventListener('change', handleClassColumnChange);
        document.getElementById('apply-mapping')?.addEventListener('click', applyMappings);
    }
});

function getExplanations(changeSummary) {
    // Check if changeSummary is null or 'null' string
    if (!changeSummary || changeSummary === 'null') {
        return {};
    }
    
    // Check different possible keys for explanations
    if (changeSummary["Mapping explanations"]) {
        return changeSummary["Mapping explanations"];
    } else if (changeSummary["Explanations"]) {
        return changeSummary["Explanations"];
    }
    return {};  // fallback empty object
}

function createResponsePanel(result) {
    const container = document.createElement('div');
    container.className = 'response-panel';
    
    // Get the summaries
    const changeSummary = result?.change_summary || {};
    const validationResults = result?.validation_results;
    const totalIssues = validationResults?.summary?.total_issues || 0;
    const explanations = getExplanations(changeSummary);
    const columns = Object.keys(explanations);
    
    // Initialize mapping overrides with the explanations
    initializeMappingOverridesFromExplanations(explanations);

    // Create main header
    const title = document.createElement('h2');
    title.className = 'title';
    
    // Format the duration nicely
    let durationText = '';
    if (result.processingDuration) {
        const minutes = Math.floor(result.processingDuration / 60);
        const seconds = Math.floor(result.processingDuration % 60);
        
        if (minutes > 0) {
            durationText = ` (${minutes} mins ${seconds} secs)`;
        } else {
            durationText = ` (${seconds} secs)`;
        }
    }
    
    title.textContent = `Census Processing Complete${durationText}`;
    container.appendChild(title);

    // Create tab buttons
    const tabButtons = document.createElement('div');
    tabButtons.className = 'response-tabs';
    
    const summaryTab = document.createElement('button');
    summaryTab.className = 'tab-button active';
    summaryTab.textContent = 'Mapping Summary';
        
    const validationTab = document.createElement('button');
    validationTab.className = 'tab-button';
    validationTab.textContent = `Validation Results (${totalIssues})`;

    const inferenceTab = document.createElement('button');
    inferenceTab.className = 'tab-button';
    const inferenceSummary = result?.sheets_summary?.inference_summary?.summary;
    const totalChanges = inferenceSummary?.total_changes || 0;
    inferenceTab.textContent = `AI Inference (${totalChanges})`;

    tabButtons.appendChild(validationTab);
    tabButtons.appendChild(summaryTab);
    tabButtons.appendChild(inferenceTab);
    
    container.appendChild(tabButtons);

    const hasValidData = changeSummary["Number of columns mapped"] > 0 || 
                        changeSummary["Number of values mapped"] > 0;
    const sheetName = extractSheetNameFromResponse(result);
    if (hasValidData && sheetName) {
        const textFormatColumns = ["Employee ID Number","Residential Zip Code","Employee Work Location Code"];
        handleSheetDownload(sheetName, 'current', textFormatColumns);        
    }
    
    // Create content containers
    const summaryContent = document.createElement('div');
    summaryContent.className = 'tab-content-census active';
    summaryContent.innerHTML = `
        <div class="change-summary">
            <div class="summary-header">
                <strong>Summary of Changes</strong>
                <table class="stats-table">
                    <tr>
                        <td>Columns Mapped: ${changeSummary["Number of columns mapped"] || 0}</td>
                        <td>Values Updated: ${changeSummary["Number of values mapped"] || 0}</td>
                    </tr>
                </table>
            </div>

            ${columns.length > 0 ? `
                <div class="mapping-table">
                    <table class="ms-Table">
                        <thead>
                            <tr>
                                <th class="ms-Table-cell">Column</th>
                                <th class="ms-Table-cell">Explanation</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${columns.map(column => `
                                <tr>
                                    <td class="ms-Table-cell original">${column}</td>
                                    <td class="ms-Table-cell mapped">${explanations[column]}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                </div>
            ` : `
                <div class="no-changes">
                    No column mappings to display
                </div>
            `}
        </div>
    `;

    // Create validation and inference content using the new functions
    const validationContent = createValidationPanel(validationResults);
    const inferenceContent = createInferenceContent(result?.sheets_summary?.inference_summary);

    // Append content containers
    container.appendChild(validationContent);
    container.appendChild(summaryContent);
    container.appendChild(inferenceContent);

    summaryTab.addEventListener('click', () => {
        selectedCensusSummaryTab = 'summary';
        summaryTab.classList.add('active');
        validationTab.classList.remove('active');
        inferenceTab.classList.remove('active');
        summaryContent.classList.add('active');
        validationContent.classList.remove('active');
        inferenceContent.classList.remove('active');
    });

    validationTab.addEventListener('click', () => {
        selectedCensusSummaryTab = 'validation';
        validationTab.classList.add('active');
        summaryTab.classList.remove('active');
        inferenceTab.classList.remove('active');
        validationContent.classList.add('active');
        summaryContent.classList.remove('active');
        inferenceContent.classList.remove('active');
    });

    inferenceTab.addEventListener('click', () => {
        selectedCensusSummaryTab = 'inference';
        inferenceTab.classList.add('active');
        summaryTab.classList.remove('active');
        validationTab.classList.remove('active');
        inferenceContent.classList.add('active');
        summaryContent.classList.remove('active');
        validationContent.classList.remove('active');
    });

    // Set initial active tab based on stored state
    if (selectedCensusSummaryTab === 'summary') {
        summaryTab.click();
    } else if (selectedCensusSummaryTab === 'inference') {
        inferenceTab.click();
    } else {
        validationTab.click();
    }

    return container;
}

// Simplified but reliable worksheet name generator
function generateWorksheetName(baseName, context) {
    // Clean the base name to ensure it's valid
    baseName = (baseName || "Census").split('_')[0].trim();
    if (baseName === "") baseName = "Census";
    
    // Pattern to match sheet names with our format: BaseName_GeneratedCensus_X
    const pattern = new RegExp(`^${baseName}_GeneratedCensus_(\\d+)$`);
    
    // Find all matching sheet numbers
    const existingNumbers = [];
    context.workbook.worksheets.items.forEach(sheet => {
        const match = sheet.name.match(pattern);
        if (match && match[1]) {
            existingNumbers.push(parseInt(match[1], 10));
        }
    });
    
    // Find the next available number (either max + 1 or start at 1)
    let nextNumber = 1;
    if (existingNumbers.length > 0) {
        nextNumber = Math.max(...existingNumbers) + 1;
    }
    
    // Create new name and verify it doesn't exist
    let newName = `${baseName}_GeneratedCensus_${nextNumber}`;
    
    // Double-check the name is unique (in case of any pattern matching issues)
    while (context.workbook.worksheets.items.some(sheet => sheet.name === newName)) {
        nextNumber++;
        newName = `${baseName}_GeneratedCensus_${nextNumber}`;
    }
    
    logMessage(`Generated worksheet name: ${newName} (next available number: ${nextNumber})`);
    return newName;
}

/**
 * Handle sheet download with improved handling to prevent duplicates and ensure spinner visibility
 * @param {string} sheetName - Name of the sheet to download
 * @param {string} type - Download type
 * @param {string[]} textFormatColumns - Columns to format as text
 */
async function handleSheetDownload(sheetName, type, textFormatColumns = []) {
    try {
        // Use a static flag to prevent multiple simultaneous downloads
        if (handleSheetDownload.isDownloading) {
            logMessage("Download already in progress, ignoring duplicate request");
            return;
        }
        
        handleSheetDownload.isDownloading = true;
        
        if (!sheetName) {
            logMessage("No sheet name provided");
            handleSheetDownload.isDownloading = false;
            return;
        }

        logMessage(`Processing sheet download: ${sheetName}, type: ${type}`);
        const cleanSheetName = sheetName.replace(/\.csv$/i, '');
        
        // Extract the base name (part before first underscore or full name)
        const baseName = cleanSheetName.split('_')[0] || cleanSheetName;

        if (type === 'current') {
            try {
                // Remove any existing loading indicators to prevent duplicates
                const existingLoadingDivs = document.querySelectorAll('#sheet-download-loading');
                existingLoadingDivs.forEach(div => div.remove());
                
                // Create and add loading indicator with enhanced visibility
                const loadingDiv = document.createElement('div');
                loadingDiv.className = 'loading-section';
                loadingDiv.id = 'sheet-download-loading';
                loadingDiv.style.display = 'flex'; // Ensure it's displayed as flex
                loadingDiv.style.padding = '15px'; // Add more padding
                loadingDiv.style.margin = '15px 0'; // Add margin
                loadingDiv.style.backgroundColor = '#f0f8ff'; // Light blue background to stand out
                loadingDiv.style.border = '1px solid #0078d4'; // Blue border
                loadingDiv.innerHTML = '<div class="spinner"></div><p style="margin-left: 10px; font-weight: bold;">Downloading converted sheet, please wait...</p>';
                
                // Add to a visible container
                const actionsPanel = document.getElementById('actions-panel');
                if (actionsPanel) {
                    // Add to the top of the actions panel for better visibility
                    if (actionsPanel.firstChild) {
                        actionsPanel.insertBefore(loadingDiv, actionsPanel.firstChild);
                    } else {
                        actionsPanel.appendChild(loadingDiv);
                    }
                } else {
                    const tabContent = document.getElementById('census-tab');
                    if (tabContent) {
                        tabContent.insertBefore(loadingDiv, tabContent.firstChild);
                    }
                }
                
                // Ensure the spinner is rendered before proceeding
                await new Promise(resolve => setTimeout(resolve, 50));
                
                const apiUrl = await ConfigManager.getApiUrl(`/download-file-json/${encodeURIComponent(sheetName)}`);
                logMessage(`Calling API ${apiUrl}`);
                
                const response = await fetch(apiUrl);
                if (!response.ok) {
                    throw new Error(`https error! status: ${response.status}`);
                }

                const rawText = await response.text();
                if (!rawText || rawText.trim() === '') {
                    throw new Error('Empty response received from server');
                }
                
                const cleanedText = rawText.replace(/:\s*NaN/g, ': null');
                logMessage(`Cleaned Raw response received: ${cleanedText.substring(0, 100)}...`);
                
                let data;
                try {
                    data = JSON.parse(cleanedText);
                    if (!Array.isArray(data) || !data.length || !Array.isArray(data[0])) {
                        throw new Error('Invalid data structure received from server');
                    }
                } catch (parseError) {
                    throw new Error(`Failed to parse JSON response: ${parseError.message}`);
                }
                
                // Clean the data (without adding single quotes)
                data = cleanExcelData(data);
                
                const rowCount = data.length;
                const colCount = data[0].length;
                logMessage(`Data parsed and cleaned - Number of rows: ${rowCount}, Number of columns: ${colCount}`);
                
                await Excel.run(async (context) => {
                    try {
                        const worksheets = context.workbook.worksheets;
                        worksheets.load("items/name");
                        await context.sync();
                        
                        const newSheetName = generateWorksheetName(baseName, context);
                        logMessage(`Creating new worksheet: ${newSheetName}`);
                        
                        const newSheet = worksheets.add(newSheetName);
                        await context.sync();
                        logMessage("Worksheet created");
                        
                        if (rowCount > 0 && colCount > 0) {
                            // Insert all data first
                            const dataRange = newSheet.getRangeByIndexes(0, 0, rowCount, colCount);
                            dataRange.values = data;
                            await context.sync();
                            logMessage("Data inserted successfully");
                            
                            // Get the header row to identify columns
                            const headerRow = data[0];
                            
                            // Define date columns that need explicit formatting
                            const dateColumns = [
                                "Census \"As Of\" Date", 
                                "Employee Date of Birth",
                                "Date of Disability", 
                                "Date Of Disability",
                                "Date of Hire"
                            ];
                            
                            // Format specified text columns
                            if (textFormatColumns && textFormatColumns.length > 0) {
                                logMessage(`Formatting specific columns as text: ${textFormatColumns.join(', ')}`);
                                
                                // Find the column indices to format
                                for (const columnName of textFormatColumns) {
                                    const columnIndex = headerRow.findIndex(header => 
                                        header === columnName || 
                                        header === columnName.trim()
                                    );
                                    
                                    if (columnIndex !== -1) {
                                        const columnRange = newSheet.getRangeByIndexes(0, columnIndex, rowCount, 1);
                                        columnRange.numberFormat = Array(rowCount).fill(['@']);
                                        await context.sync();
                                        logMessage(`Applied text format to column: ${columnName}`);
                                    } else {
                                        logMessage(`Warning: Column '${columnName}' not found in the data`);
                                    }
                                }
                            }
                            
                            // Format date columns explicitly as M/D/YYYY
                            for (const columnName of dateColumns) {
                                const columnIndex = headerRow.findIndex(header => 
                                    header === columnName || 
                                    header === columnName.trim()
                                );
                                
                                if (columnIndex !== -1) {
                                    const columnRange = newSheet.getRangeByIndexes(1, columnIndex, rowCount - 1, 1);
                                    // Apply explicit M/D/YYYY format
                                    columnRange.numberFormat = Array(rowCount - 1).fill(['m/d/yyyy']);
                                    await context.sync();
                                    logMessage(`Applied date format to column: ${columnName}`);
                                } else {
                                    logMessage(`Warning: Date column '${columnName}' not found in the data`);
                                }
                            }
                            
                            // Optimize column widths for better readability
                            dataRange.format.autofitColumns();
                            await context.sync();
                        }

                        // Activate the new sheet
                        newSheet.activate();
                        await context.sync();
                        
                        logMessage("Sheet creation completed successfully");
                        showSuccessToast(`Data imported to new sheet "${newSheetName}"`);
                        
                    } catch (innerError) {
                        logMessage(`Excel operation error: ${innerError.message}`);
                        console.error("Inner Excel operation failed:", innerError);
                        throw innerError;
                    }
                });

            } catch (error) {
                logMessage(`Error: ${error.message}`);
                console.error("Error handling sheet download:", error);
                showErrorToast(`Error importing sheet: ${error.message}`);
            } finally {
                // Use setTimeout to ensure the loading indicator stays visible for at least 500ms
                setTimeout(() => {
                    // Remove loading indicator
                    const loadingDiv = document.getElementById('sheet-download-loading');
                    if (loadingDiv) {
                        loadingDiv.remove();
                    }
                    
                    // Reset the downloading flag
                    handleSheetDownload.isDownloading = false;
                }, 500); // Keep visible for at least 500ms
            }
        }
    } catch (error) {
        console.error("Outer error in handleSheetDownload:", error);
        showErrorToast(`Failed to download sheet: ${error.message}`);
        
        // Final attempt to clean up the loading indicator
        const loadingDivs = document.querySelectorAll('#sheet-download-loading');
        loadingDivs.forEach(div => div.remove());
        
        // Reset the downloading flag
        handleSheetDownload.isDownloading = false;
    }
}

// Initialize the static flag
handleSheetDownload.isDownloading = false;

// Helper function to clean data without adding single quotes
function cleanExcelData(data) {
    return data.map(row => 
        row.map(cell => {
            // Handle null values
            if (cell === null || cell === undefined || Number.isNaN(cell)) {
                return "";
            }
            
            // Handle Infinity
            if (typeof cell === 'number' && !Number.isFinite(cell)) {
                return "";
            }
            
            // Just return the value as is
            return cell;
        })
    );
}



function logMessage(message) {
    console.log(message);
    const logDisplay = document.getElementById("log-display");
    const loggingEnabled = document.getElementById("enable-logging")?.checked;
    
    if (logDisplay && loggingEnabled) {
        const timestamp = new Date().toLocaleTimeString();
        logDisplay.style.display = "block";
        const newMessage = document.createElement('div');
        newMessage.textContent = `${timestamp}: ${message}`;
        logDisplay.appendChild(newMessage);
        logDisplay.scrollTop = logDisplay.scrollHeight;
    } else if (logDisplay) {
        logDisplay.style.display = "none";
    }
}

function displayProducts(products) {
    const productsContainer = document.getElementById("products-container");
    
    // Clear existing content
    productsContainer.innerHTML = '';
    
    // Create checkbox elements
    products.forEach(product => {
        const div = document.createElement('div');
        div.className = 'product-checkbox';
        
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.id = product.id;
        input.name = 'product';
        input.value = product.id;
        input.checked = product.checked;
        input.className = 'ms-CheckBox';
        
        const label = document.createElement('label');
        label.htmlFor = product.id;
        label.textContent = product.name;
        label.className = 'ms-Label';
        
        div.appendChild(input);
        div.appendChild(label);
        productsContainer.appendChild(div);
    });
}

async function fetchProducts() {
    try {
        logMessage("Fetching products...");
        const apiUrl = await ConfigManager.getApiUrl('/get-products');
        logMessage("Fetching API: "+apiUrl);
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`https error! status: ${response.status}`);
        }

        const result = await response.json();
        logMessage("Products fetched successfully");
        
        displayProducts(result.products);
    } catch (error) {
        logMessage("Error fetching products: " + error.message);
        console.error("Error fetching products:", error);
        const productsContainer = document.getElementById("products-container");
        productsContainer.innerHTML = '<div class="error-message">Failed to load products. Please refresh the page.</div>';
    }
}

function initializeProducts() {
    const productSelect = document.getElementById("products-select");
    
    // Clear any existing options
    productSelect.innerHTML = '';
    
    const products = [
        "GIP", "Vision", "Dental", "GCI", "GHI", "GACC",
        "SMP", "LTD", "STD", "GTL", "GPLW", "GPLU"
    ];
    
    // Create and sort DocumentFragment for better performance
    const fragment = document.createDocumentFragment();
    
    products.sort().forEach(product => {
        const option = document.createElement("option");
        option.value = product;
        option.textContent = product;
        fragment.appendChild(option);
    });
    
    productSelect.appendChild(fragment);
}

// Store mappings globally with validation rules
const CLASS_MAPPING_CONFIG = {
    maxDescriptionLength: 100,
    validationRules: {
        required: true,
        pattern: /^[a-zA-Z0-9\s\-_]+$/,
        minLength: 3
    }
};

let classMappings = new Map();



function validateMapping(value) {
    if (value.trim().length > CLASS_MAPPING_CONFIG.maxDescriptionLength) {
        return 'Description is too long';
    }
    if (CLASS_MAPPING_CONFIG.validationRules.required && !value.trim()) {
        return 'Description is required';
    }
    if (!CLASS_MAPPING_CONFIG.validationRules.pattern.test(value)) {
        return 'Description contains invalid characters';
    }
    if (value.trim().length < CLASS_MAPPING_CONFIG.validationRules.minLength) {
        return 'Description is too short';
    }
    return null;
}

function validateInput(event) {
    const input = event.target;
    const errorDiv = input.nextElementSibling;
    const error = validateMapping(input.value);
    
    errorDiv.textContent = error || '';
    input.classList.toggle('invalid', !!error);
    
    // Update apply button state
    const applyButton = document.getElementById('apply-mapping');
    const hasErrors = document.querySelectorAll('.mapping-input.invalid').length > 0;
    applyButton.disabled = hasErrors;
}

async function initializeMappingOverrides() {
    try {
        const sourceColumns = await getSourceColumns();
        const uniqueColumns = await fetchUniqueColumns(); // Keep this for custom mappings
        const mappingsBody = document.getElementById('mapping-overrides-body');
        
        if (!mappingsBody) {
            console.error('Mapping overrides body not found');
            return;
        }

        // Get all columns from the active worksheet
        const allDestinationColumns = await Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = sheet.getUsedRange();
            range.load("values");
            await context.sync();
            return range.values[0] || [];
        });

        // Clear existing rows
        mappingsBody.innerHTML = '';

        // Create a row for each column from the worksheet
        for (const destColumn of allDestinationColumns) {
            if (destColumn) {
                const sourceColumn = columnMappings.get(destColumn) || '';
                const row = await createMappingRow(
                    allDestinationColumns, 
                    sourceColumns, 
                    destColumn,
                    sourceColumn
                );
                mappingsBody.appendChild(row);
            }
        }

        // Initialize the add button - using uniqueColumns for custom mappings
        const addButton = document.querySelector('.add-mapping-button');
        if (addButton) {
            const newAddButton = addButton.cloneNode(true);
            addButton.parentNode.replaceChild(newAddButton, addButton);
            
            newAddButton.onclick = async (e) => {
                e.preventDefault();
                const tbody = document.getElementById('mapping-overrides-body');
                if (tbody) {
                    // Use uniqueColumns here for custom mappings
                    const newRow = await createMappingRow(uniqueColumns, sourceColumns);
                    tbody.appendChild(newRow);
                }

                // Get the containing mapping-table div and scroll it to the bottom
                const mappingTable = tbody.closest('.mapping-table');
                if (mappingTable) {
                    mappingTable.scrollTop = mappingTable.scrollHeight;
                }
            };
        }
    } catch (error) {
        console.error('Error initializing mapping overrides:', error);
        logMessage(`Error initializing mapping overrides: ${error.message}`);
    }
}

function switchTab(tabName) {
    // Update button states
    document.querySelectorAll('.tab-button').forEach(button => {
        button.classList.toggle('active', button.dataset.tab === tabName);
    });

    // Update content visibility
    document.querySelectorAll('.tab-content').forEach(content => {
        content.style.display = content.id === `${tabName}-tab` ? 'block' : 'none';
    });

    // Initialize content based on tab
    if (tabName === 'mapping-overrides') {
        initializeMappingOverrides();
    } else if (tabName === 'class-mapping') {
        refreshClassColumns();
    }
    
    // If returning to census tab, restore the census summary tab state
    if (tabName === 'census') {
        const actionsPanel = document.getElementById('actions-panel');
        if (actionsPanel && actionsPanel.style.display !== 'none') {
            // Find the correct tab based on stored state and click it
            const tabToClick = actionsPanel.querySelector(
                selectedCensusSummaryTab === 'validation' ? '.response-tabs button:nth-child(2)' :
                selectedCensusSummaryTab === 'inference' ? '.response-tabs button:nth-child(3)' :
                '.response-tabs button:first-child'
            );
            if (tabToClick) {
                tabToClick.click();
            }
        }
    }
}


// Update the function to remove automatic tab switching
function initializeMappingOverridesFromExplanations(explanations) {
    try {
        // Clear existing mappings
        columnMappings.clear();

        // Process each explanation to extract mapping
        Object.entries(explanations).forEach(([destColumn, explanation]) => {
            let currentSource = '';
            let match = explanation.match(/Mapped from '([^']+)'/);
            if (!match) {
                match = explanation.match(/mapping from source: '([^']+)'/);
            }
            
            if (match) {
                currentSource = match[1];
                columnMappings.set(destColumn, currentSource);
            }
        });
    } catch (error) {
        console.error('Error initializing mapping overrides:', error);
        logMessage(`Error initializing mapping overrides: ${error.message}`);
    }
}

async function refreshClassColumns() {
    try {
        await Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = sheet.getUsedRange();
            range.load(["values", "columnCount"]);
            await context.sync();

            const headers = range.values[0];
            // Find headers ending with ' Class' but not ' Class Name'
            const classColumns = headers
                .filter(header => 
                    header && 
                    header.toString().endsWith(' Class') &&
                    !header.toString().endsWith(' Class Name')
                )
                .map(header => header.toString().replace(' Class', '')); // Extract product name

            const select = document.getElementById('class-column-select');
            select.innerHTML = '<option value="">Select a product...</option>';
            
            // Remove duplicates and sort
            [...new Set(classColumns)].sort().forEach(product => {
                const option = document.createElement('option');
                option.value = product;
                option.textContent = product;
                select.appendChild(option);
            });

            // Restore previous selection if exists
            const previousSelection = select.dataset.lastSelected;
            if (previousSelection && classColumns.includes(previousSelection)) {
                select.value = previousSelection;
                handleClassColumnChange({ target: select });
            }
        });
    } catch (error) {
        console.error('Error refreshing class columns:', error);
        document.getElementById('class-column-error').textContent = 'Error loading columns. Please try again.';
    }
}

// Add this debug helper
function debugButton() {
    const button = document.getElementById('apply-mapping');
    if (!button) {
        console.error('Apply button not found');
        return;
    }
    
    // Log button state
    console.log('Button state:', {
        disabled: button.disabled,
        display: button.style.display,
        listeners: button.getEventListeners
    });
}

function handleClassColumnChange(event) {
    const selectedProduct = event.target.value;
    const tableDiv = document.getElementById('class-mapping-table');
    
    if (!selectedProduct) {
        tableDiv.style.display = 'none';
        return;
    }

    event.target.dataset.lastSelected = selectedProduct;
    logMessage(`Handling class column change for product: ${selectedProduct}`);

    try {
        Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = sheet.getUsedRange();
            range.load("values");
            await context.sync();

            const headers = range.values[0];
            const columnIndex = headers.findIndex(header => header === `${selectedProduct} Class`);
            
            if (columnIndex === -1) return;

            // Get unique values
            const uniqueValues = [...new Set(range.values
                .slice(1)
                .map(row => row[columnIndex])
                .filter(value => value != null && value.toString().trim() !== '')
                .map(value => value.toString().trim())
            )].sort();

            // Create table HTML with editable ID column
            const tableHTML = `
                <table class="ms-Table">
                    <thead>
                        <tr>
                            <th class="ms-Table-cell">Unique Classes</th>
                            <th class="ms-Table-cell">Class Number</th>
                            <th class="ms-Table-cell">Class Name</th>
                        </tr>
                    </thead>
                    <tbody id="class-mapping-body">
                        ${uniqueValues.map((value, index) => {
                            const defaultClassId = `${index + 1}`;
                            const savedMappings = classMappings.get(`${selectedProduct} Class`) || new Map();
                            const savedMapping = savedMappings.get(value) || {};
                            const savedId = savedMapping.classId || '';
                            const savedValue = savedMapping.className || '';
                            
                            return `
                                <tr>
                                    <td class="ms-Table-cell">${value}</td>
                                    <td class="ms-Table-cell">
                                        <input type="text" 
                                               class="mapping-input id-input"
                                               value="${savedId}">
                                    </td>
                                    <td class="ms-Table-cell">
                                        <input type="text" 
                                               class="mapping-input name-input" 
                                               data-class-name="${value}"
                                               value="${savedValue}"
                                               maxlength="${CLASS_MAPPING_CONFIG.maxDescriptionLength}">
                                        <div class="error-message"></div>
                                    </td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
                <div class="button-container">
                    <button id="apply-mapping" class="ms-Button ms-Button--primary">
                        <span class="ms-Button-label">Apply Mappings</span>
                    </button>
                </div>
            `;

            tableDiv.innerHTML = tableHTML;

            // Add input validation listeners
            document.querySelectorAll('.name-input').forEach(input => {
                input.addEventListener('input', validateInput);
                input.addEventListener('blur', validateInput);
            });

            // Add click handler for the apply button
            const applyButton = document.getElementById('apply-mapping');
            if (applyButton) {
                // Remove any existing listeners first
                const newButton = applyButton.cloneNode(true);
                applyButton.parentNode.replaceChild(newButton, applyButton);
                
                // Add new click listener
                newButton.addEventListener('click', () => {
                    logMessage('Apply button clicked');
                    applyMappings();
                });
            } else {
                logMessage('Error: Apply button not found after table creation');
            }

            tableDiv.style.display = 'block';
        });
    } catch (error) {
        console.error('Error handling column change:', error);
        logMessage(`Error in handleClassColumnChange: ${error.message}`);
        document.getElementById('class-column-error').textContent = 'Error loading class values. Please try again.';
    }
}

async function applyMappings() {
    const selectedProduct = document.getElementById("class-column-select").value;
    const newMappings = new Map();
    let hasErrors = false;

    logMessage("Starting to apply mappings for product: " + selectedProduct);

    // Validate and collect all mappings
    document.querySelectorAll("tr").forEach(row => {
        const sourceValue = row.querySelector("td:first-child")?.textContent;
        const idInput = row.querySelector(".id-input");
        const nameInput = row.querySelector(".name-input");
        
        if (!sourceValue || !idInput || !nameInput) return;

        const error = validateMapping(nameInput.value);
        if (error && nameInput.value.trim() !== "") {
            nameInput.nextElementSibling.textContent = error;
            nameInput.classList.add("invalid");
            hasErrors = true;
        } else {
            newMappings.set(sourceValue, {
                classId: idInput.value.trim() || idInput.dataset.originalId,
                className: nameInput.value.trim()
            });
        }
    });

    if (hasErrors) {
        logMessage("Validation errors found. Mappings not applied.");
        return;
    }

    // Update Excel with new mappings
    Excel.run(async (context) => {
        try {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = sheet.getUsedRange();
            range.load("values");
            await context.sync();

            const headers = range.values[0];
            const classColumnIndex = headers.findIndex(header => header === `${selectedProduct} Class`);
            const classNameColumnIndex = headers.findIndex(header => header === `${selectedProduct} Class Name`);
            
            if (classColumnIndex === -1) {
                logMessage("Class column not found. Mappings not applied.");
                return;
            }

            // Get ranges for both columns
            const classRange = sheet.getRangeByIndexes(1, classColumnIndex, range.values.length - 1, 1);
            const classNameRange = classNameColumnIndex !== -1 ? 
                sheet.getRangeByIndexes(1, classNameColumnIndex, range.values.length - 1, 1) : null;
            
            classRange.load("values");
            if (classNameRange) classNameRange.load("values");
            await context.sync();

            // Store original values and their row indices for later use
            const originalValueToRows = new Map();
            classRange.values.forEach(([cellValue], index) => {
                if (cellValue) {
                    const key = cellValue.toString().trim();
                    if (!originalValueToRows.has(key)) {
                        originalValueToRows.set(key, []);
                    }
                    originalValueToRows.get(key).push(index);
                }
            });

            // Prepare updates for both columns
            const newClassValues = classRange.values.map(([cellValue]) => {
                if (!cellValue) return [cellValue];
                const key = cellValue.toString().trim();
                const mapping = newMappings.get(key);
                return mapping ? [mapping.classId] : [cellValue];
            });

            // Update Class Name column if it exists and has mappings
            let newClassNameValues = [];
            if (classNameRange) {
                newClassNameValues = classNameRange.values.map((currentValue, rowIndex) => {
                    // Find the original value for this row
                    const originalValue = classRange.values[rowIndex][0]?.toString().trim();
                    if (!originalValue) return [currentValue[0]];

                    const mapping = newMappings.get(originalValue);
                    if (mapping && mapping.className) {
                        return [mapping.className];
                    }
                    return [currentValue[0]];
                });
            }

            // Apply updates in order
            if (classNameRange) {
                classNameRange.values = newClassNameValues;
            }
            classRange.values = newClassValues;

            await context.sync();

            // Show success message
            const messageDiv = document.createElement("div");
            messageDiv.className = "success-message";
            messageDiv.textContent = "Mappings applied successfully";
            document.querySelector(".class-mapping-container").appendChild(messageDiv);
            setTimeout(() => messageDiv.remove(), 3000);
            logMessage("Mappings applied successfully for product: " + selectedProduct);

        } catch (error) {
            console.error("Error applying mappings:", error);
            const errorDiv = document.createElement("div");
            errorDiv.className = "error-message";
            errorDiv.textContent = "Error applying mappings. Please try again.";
            document.querySelector(".class-mapping-container").appendChild(errorDiv);
            setTimeout(() => errorDiv.remove(), 3000);
            logMessage("Error applying mappings: " + error.message);
        }
    });
}

function createValidationErrorPanel(validationError, validationContent) {
    // Calculate total missing columns
    let totalMissingColumns = 0;
    const productMissingColumns = {};

    // Check for missing columns in the failure_summary
    logMessage("Processing missing columns from validation_results missing_columns");
    Object.entries(validationError.missing_columns).forEach(([product, columns]) => {
        productMissingColumns[product] = columns;
        totalMissingColumns += columns.length;
    });

    // Create header with total missing columns count
    if(totalMissingColumns > 0)
    {
        const container = document.createElement('div');
        container.className = 'validation-error-panel';

        const header = document.createElement('h2');
        header.className = 'error-title';
        header.innerHTML = `Census Validation Failed 
            <span class="missing-columns-badge">${totalMissingColumns} Missing Columns</span>`;

        const subheader = document.createElement('p');
        subheader.className = 'error-subtitle';
        subheader.textContent = 'The following mandatory columns have no values:';

        const errorList = document.createElement('div');
        errorList.className = 'error-product-list';

        // Group products by their missing columns
        const columnToProducts = {};
        Object.entries(productMissingColumns).forEach(([product, columns]) => {
            const columnKey = columns.sort().join(',');
            if (!columnToProducts[columnKey]) {
                columnToProducts[columnKey] = [];
            }
            columnToProducts[columnKey].push(product);
        });

        // Create sections for each unique set of missing columns
        Object.entries(columnToProducts).forEach(([columnKey, products]) => {
            const section = document.createElement('div');
            section.className = 'error-product-section';

            const header = document.createElement('h3');
            header.className = 'error-product-name';
            if (products.length > 1) {
                header.innerHTML = `Products: <strong>${products.join(', ')}</strong>`;
            } else {
                header.innerHTML = `Product: <strong>${products[0]}</strong>`;
            }

            const columnList = document.createElement('ul');
            columnList.className = 'error-column-list';

            columnKey.split(',').forEach(column => {
                const columnItem = document.createElement('li');
                columnItem.innerHTML = `
                    <span class="column-name">${column}</span>
                    <span class="column-count-badge">${products.length}</span>
                `;
                columnList.appendChild(columnItem);
            });

            section.appendChild(header);
            section.appendChild(columnList);
            errorList.appendChild(section);
        });

        // Add action buttons or suggestions
        const actionPanel = document.createElement('div');
        actionPanel.className = 'error-action-panel';
        
        container.appendChild(header);
        container.appendChild(subheader);
        container.appendChild(errorList);
        container.appendChild(actionPanel);

        attachValidationClickHandlers(validationContent);
        return container;
    }
}


function attachValidationClickHandlers(validationContent) {
    const issueItems = validationContent.querySelectorAll('.validation-issues li');
    issueItems.forEach(item => {
        item.addEventListener('click', async () => {
            // Remove highlighted state from all items
            issueItems.forEach(i => i.classList.remove('highlighted'));
            
            // Add loading state
            item.classList.add('loading');
            
            try {
                const row = parseInt(item.dataset.row);
                const column = item.dataset.column;
                await highlightCell(row, column);
                
                // Show success state
                item.classList.add('highlighted');
            } catch (error) {
                console.error('Error highlighting cell:', error);
            } finally {
                // Remove loading state
                item.classList.remove('loading');
            }
        });
    });
}

// Add these helper functions
function getColumnIndex(columnName) {
    return new Promise((resolve, reject) => {
        Excel.run(async (context) => {
            try {
                const sheet = context.workbook.worksheets.getActiveWorksheet();
                const range = sheet.getUsedRange();
                range.load("values");
                await context.sync();

                const headers = range.values[0];
                const columnIndex = headers.findIndex(header => header === columnName);
                
                if (columnIndex === -1) {
                    throw new Error(`Column ${columnName} not found`);
                }
                
                resolve(columnIndex);
            } catch (error) {
                reject(error);
            }
        });
    });
}

async function highlightCell(row, column) {
    try {
        await Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const columnIndex = await getColumnIndex(column);
            const range = sheet.getCell(row - 1, columnIndex);
            range.select();
            await context.sync();
        });
    } catch (error) {
        console.error(`Error highlighting cell: ${error.message}`);
    }
}

function extractSheetNameFromResponse(result) {
    if (!result) {
        logMessage("extractSheetNameFromResponse: Result is null or undefined");
        return null;
    }

    // Debug log the available keys
    logMessage("extractSheetNameFromResponse: Available keys: " + Object.keys(result).join(', '));

    // Look inside sheets_summary
    if (result.sheets_summary) {
        logMessage("extractSheetNameFromResponse: Checking sheets_summary keys: " + Object.keys(result.sheets_summary).join(', '));
        
        const sheetNameKey = Object.keys(result.sheets_summary)
            .find(key => key.startsWith('sheet_name_'));

        if (sheetNameKey) {
            const sheetName = result.sheets_summary[sheetNameKey];
            logMessage(`extractSheetNameFromResponse: Found sheet name ${sheetName} with key ${sheetNameKey}`);
            return sheetName;
        }
    }

    logMessage("extractSheetNameFromResponse: No sheet name key found");
    return null;
}


async function createOverridesTable(explanations) {
    try {
        logMessage("Starting createOverridesTable function");
        const sourceColumns = await getSourceColumns();
        const uniqueColumns = await fetchUniqueColumns();
        
        // Filter out any explanations that have no valid mapping
        const validExplanations = Object.entries(explanations)
            .filter(([destColumn, explanation]) => {
                let match = explanation.match(/Mapped from '([^']+)'/);
                if (!match) {
                    match = explanation.match(/mapping from source: '([^']+)'/);
                }
                return destColumn && destColumn !== 'Select a column...' && match;
            });
        
        // Create main container
        const container = document.createElement('div');
        
        // First create the existing mappings table
        const existingMappingsTable = document.createElement('div');
        existingMappingsTable.className = 'mapping-table';
        existingMappingsTable.innerHTML = `
            <table class="ms-Table">
                <thead>
                    <tr>
                        <th class="ms-Table-cell">Destination Column</th>
                        <th class="ms-Table-cell">Source Column</th>
                    </tr>
                </thead>
                <tbody>
                    ${validExplanations.map(([destColumn, explanation]) => {
                        let currentSource = '';
                        let match = explanation.match(/Mapped from '([^']+)'/);
                        if (!match) {
                            match = explanation.match(/mapping from source: '([^']+)'/);
                        }
                        
                        if (match) {
                            currentSource = match[1];
                            columnMappings.set(destColumn, currentSource);
                        }
                        
                        return `
                            <tr>
                                <td class="ms-Table-cell">${destColumn}</td>
                                <td class="ms-Table-cell">
                                    <select class="source-column-select" data-dest="${destColumn}">
                                        <option value="">Select a column...</option>
                                        ${sourceColumns.map(col => `
                                            <option value="${col}" ${col === currentSource ? 'selected' : ''}>
                                                ${col}
                                            </option>
                                        `).join('')}
                                    </select>
                                </td>
                            </tr>
                        `;
                    }).join('')}
                </tbody>
            </table>
        `;
        container.appendChild(existingMappingsTable);

        // Add custom mapping section
        const divider = document.createElement('div');
        divider.className = 'custom-mappings-divider';
        divider.innerHTML = '<h3 class="custom-mappings-title">Custom Column Mappings</h3>';
        container.appendChild(divider);

        const buttonLink = document.createElement('a');
        buttonLink.href = '#';
        buttonLink.className = 'add-mapping-button';
        buttonLink.textContent = '+ Add Custom Mapping';

        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'add-mapping-button-container';
        buttonContainer.appendChild(buttonLink);

        container.appendChild(buttonContainer);

        return {
            html: container.outerHTML,
            addButtonSelector: '.add-mapping-button',
            uniqueColumns: uniqueColumns,
            sourceColumns: sourceColumns
        };
    } catch (error) {
        logMessage(`Error creating overrides table: ${error.message}`);
        console.error("Error creating overrides table:", error);
        return '<div class="error-message">Error loading mapping overrides</div>';
    }
}

async function getSourceColumns() {
    return await Excel.run(async (context) => {
        const worksheets = context.workbook.worksheets;
        worksheets.load("items/name");
        await context.sync();

        const sheet = worksheets.items.find(sheet => sheet.name.toLowerCase() === 'census');
        if (!sheet) throw new Error("Census sheet not found - please ensure you have a sheet named 'Census'");

        const range = sheet.getUsedRange();
        range.load("values");
        await context.sync();

        return range.values[0];
    });
}

async function updateColumnValues(columnName, newValues) {
    await Excel.run(async (context) => {
        const sheet = context.workbook.worksheets.getActiveWorksheet();
        const range = sheet.getUsedRange();
        range.load("values");
        await context.sync();
        
        let columnIndex = range.values[0].indexOf(columnName);
        
        // If column doesn't exist, add it
        if (columnIndex === -1) {
            logMessage(`Column ${columnName} not found, adding new column`);
            // Get the next available column
            columnIndex = range.values[0].length;
            
            // Add the header
            const headerCell = sheet.getCell(0, columnIndex);
            headerCell.values = [[columnName]];
            
            // Initialize the column with empty values
            const newColumnRange = sheet.getRangeByIndexes(1, columnIndex, range.values.length - 1, 1);
            newColumnRange.values = Array(range.values.length - 1).fill(['']);
            
            await context.sync();
            logMessage(`Added new column ${columnName} at index ${columnIndex}`);
        }
        
        // Now update the values
        const dataRange = sheet.getRangeByIndexes(1, columnIndex, newValues.length, 1);
        dataRange.values = newValues.map(value => [value]);
        await context.sync();
        logMessage(`Updated ${newValues.length} values in column ${columnName}`);
    });
}

function checkDuplicateHeaders(headers) {
    // Add this before converting to JSON
    const uniqueHeaderCounts = {};
    headers.forEach(header => {
       uniqueHeaderCounts[header] = (uniqueHeaderCounts[header] || 0) + 1;
    });
    
    const duplicates = Object.entries(uniqueHeaderCounts)
       .filter(([_, count]) => count > 1)
       .map(([header]) => header);
    
    if (duplicates.length > 0) {
       throw new Error(`<p>Error: Duplicate column headers found: <b>${duplicates.join(', ')}</b>. Please ensure all column headers are unique.</p>`);
    }
    return false;
 }
 

 async function fetchUniqueColumns() {
    try {
        logMessage(`Called fetchUniqueColumns`);
        const apiUrl = await ConfigManager.getApiUrl('/get-unique-columns');
        logMessage(`API URL for fetching unique columns: ${apiUrl}`);
        const response = await fetch(apiUrl);        
        
        if (!response.ok) {
            throw new Error(`https error! status: ${response.status}`);
        }
        const data = await response.json();
        return data.columns || [];
    } catch (error) {
        console.error('Error fetching unique columns:', error);
        logMessage(`Error fetching unique columns: `+error);
        return [];
    }
}

async function transformColumn(targetColumn, selectedValue) {
    try {
        // First, validate we have both columns
        if (!targetColumn || !selectedValue) {
            logMessage(`Invalid parameters: targetColumn=${targetColumn}, selectedValue=${selectedValue}`);
            showMappingError("Both target and source columns must be specified", 5000);
            return;
        }

        logMessage(`Starting column transformation: ${targetColumn} from ${selectedValue}`);
        
        // Sanitize the targetColumn name (remove special characters that might cause pattern issues)
        const sanitizedTarget = sanitizeColumnName(targetColumn);
        
        // Get values from the source column with better error handling
        let sourceValues;
        try {
            sourceValues = await getColumnValues(selectedValue);
            
            // Check if we got any values
            if (!sourceValues || sourceValues.length === 0) {
                logMessage(`No values found in source column ${selectedValue}`);
                showMappingError(`No values found in source column "${selectedValue}"`, 5000);
                return;
            }
            
            // Sanitize values to ensure they're all valid strings
            sourceValues = sanitizeValues(sourceValues);
            
            if (sourceValues.length === 0) {
                logMessage(`No valid values found in source column ${selectedValue} after sanitization`);
                showMappingError(`No valid values found in source column "${selectedValue}"`, 5000);
                return;
            }
            
            logMessage(`Retrieved ${sourceValues.length} values from ${selectedValue}`);
        } catch (error) {
            logMessage(`Error retrieving source column values: ${error.message}`);
            showMappingError(`Error reading source column: ${error.message}`, 5000);
            return;
        }

        // Call the API with error handling
        try {
            const apiUrl = await ConfigManager.getApiUrl('/transform-column');
            logMessage(`Calling transform API: ${apiUrl}`);
            
            const requestData = {
                target_column: sanitizedTarget,
                values: sourceValues.slice(0, 1000) // Limit to 1000 values to prevent API issues
            };
            
            // Log the exact request payload for debugging
            logMessage(`Transform request payload: ${JSON.stringify(requestData)}`);
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                logMessage(`API error (${response.status}): ${errorText}`);
                showMappingError(`Error from transformation service: ${response.status} ${response.statusText}`, 5000);
                return;
            }
            
            const result = await response.json();
            logMessage(`Transform API response received`);
            
            if (result.transformed_values && Array.isArray(result.transformed_values)) {
                logMessage(`Updating column ${targetColumn} with ${result.transformed_values.length} values`);
                await updateColumnValues(targetColumn, result.transformed_values);
                logMessage(`Column ${targetColumn} updated successfully`);
                
                // Show success message
                showSuccessToast(`Column "${targetColumn}" updated successfully`);
            } else {
                logMessage(`No transformed values returned from API`);
                showMappingError(`No transformed values returned for "${targetColumn}"`, 5000);
            }
        } catch (error) {
            logMessage(`Error in transform API call: ${error.message}`);
            showMappingError(`Transform error: ${error.message}`, 5000);
        }
    } catch (error) {
        logMessage(`Error transforming column: ${error.message}`);
        showMappingError(`Failed to transform column: ${error.message}`, 5000);
    }
}

// Helper functions for sanitization
function sanitizeColumnName(name) {
    // Remove any characters that might cause pattern validation errors
    if (!name) return "";
    return String(name).trim().replace(/[^\w\s-]/g, '_');
}

function sanitizeValues(values) {
    // Make sure all values are valid strings
    if (!Array.isArray(values)) return [];
    
    return values
        .filter(val => val !== null && val !== undefined)
        .map(val => String(val).trim())
        .filter(val => val !== '');
}

// Success toast function to match the error toast
function showSuccessToast(message, duration = 3000) {
    // Remove any existing toasts
    const existingToasts = document.querySelectorAll('.success-toast, .error-toast');
    existingToasts.forEach(toast => toast.remove());
    
    // Create new toast
    const toast = document.createElement('div');
    toast.className = 'success-toast';
    toast.textContent = message;
    toast.style.position = 'fixed';
    toast.style.bottom = '20px';
    toast.style.right = '20px';
    toast.style.backgroundColor = '#DFF6DD';
    toast.style.color = '#107C10';
    toast.style.padding = '12px 16px';
    toast.style.borderRadius = '4px';
    toast.style.boxShadow = '0 2px 10px rgba(0,0,0,0.2)';
    toast.style.zIndex = '1000';
    toast.style.maxWidth = '350px';
    toast.style.animation = 'slideIn 0.3s ease-out';
    
    // Add to DOM
    document.body.appendChild(toast);
    
    // Auto-dismiss
    setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(100%)';
        toast.style.transition = 'all 0.3s ease-out';
        
        // Remove from DOM after transition
        setTimeout(() => {
            toast.remove();
        }, 300);
    }, duration);
}

// Improved function to get column values with better error handling
async function getColumnValues(columnName) {
    if (!columnName) {
        throw new Error("Column name must be provided");
    }
    
    logMessage(`Getting values for column: ${columnName}`);
    
    return await Excel.run(async (context) => {
        try {
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");
            await context.sync();
            
            const sheet = worksheets.items.find(sheet => sheet.name === 'Census');
            if (!sheet) {
                throw new Error("Census sheet not found - please ensure you have a sheet named 'Census'");
            }

            const range = sheet.getUsedRange();
            range.load("values");
            await context.sync();
            
            const headers = range.values[0];
            const columnIndex = headers.indexOf(columnName);
            
            if (columnIndex === -1) {
                throw new Error(`Column '${columnName}' not found in the Census sheet`);
            }
            
            const values = range.values.slice(1)
                .map(row => row[columnIndex])
                .filter(value => value !== undefined && value !== '');
            
            logMessage(`Retrieved ${values.length} values from column ${columnName}`);
            return values;
        } catch (error) {
            logMessage(`Error in getColumnValues: ${error.message}`);
            throw error;
        }
    });
}

// Also update the related functions

async function createMappingRow(destinationColumns, sourceColumns, existingDest = '', existingSource = '') {
    const row = document.createElement('tr');
    
    // Create cells with select elements
    const destCell = document.createElement('td');
    const sourceCell = document.createElement('td');
    const removeCell = document.createElement('td');
    
    destCell.className = 'ms-Table-cell';
    sourceCell.className = 'ms-Table-cell';
    removeCell.className = 'ms-Table-cell';
    
    // Create destination select 
    const destSelect = document.createElement('select');
    destSelect.className = 'destination-column-select';
    destSelect.innerHTML = '<option value="">Select a column...</option>';
    destinationColumns.forEach(col => {
        const option = document.createElement('option');
        option.value = col;
        option.textContent = col;
        if (col === existingDest) option.selected = true;
        destSelect.appendChild(option);
    });
    destCell.appendChild(destSelect);

    // Create source select - always use sourceColumns (from Census sheet)
    const sourceSelect = document.createElement('select');
    sourceSelect.className = 'source-column-select';
    sourceSelect.innerHTML = '<option value="">Select a column...</option>';
    sourceColumns.forEach(col => {
        const option = document.createElement('option');
        option.value = col;
        option.textContent = col;
        if (col === existingSource) option.selected = true;
        sourceSelect.appendChild(option);
    });
    sourceCell.appendChild(sourceSelect);

    // Create remove button
    const removeButton = document.createElement('button');
    removeButton.className = 'ms-Button ms-Button--icon';
    removeButton.innerHTML = '✕';
    removeButton.onclick = async () => {
        const columnToRemove = destSelect.value;
        if (columnToRemove) {
            // First remove from the mapping
            columnMappings.delete(columnToRemove);
            
            // Then remove the UI row
            row.remove();
            
            // Now delete the actual column in Excel
            try {
                await Excel.run(async (context) => {
                    const sheet = context.workbook.worksheets.getActiveWorksheet();
                    const range = sheet.getUsedRange();
                    range.load("values");
                    await context.sync();
                    
                    const headers = range.values[0];
                    const columnIndex = headers.indexOf(columnToRemove);
                    
                    if (columnIndex !== -1) {
                        // Convert zero-based index to column letter (A=0, B=1, etc.)
                        const columnAddress = columnIndexToLetter(columnIndex);
                        
                        // Get the entire column and delete it
                        const columnRange = sheet.getRange(`${columnAddress}:${columnAddress}`);
                        columnRange.delete("Left");
                        await context.sync();
                        
                        logMessage(`Deleted column: ${columnToRemove}`);
                        showSuccessToast(`Column "${columnToRemove}" deleted`);
                    }
                });
            } catch (error) {
                logMessage(`Error deleting column: ${error.message}`);
                console.error("Error deleting column:", error);
                showErrorToast(`Failed to delete column: ${error.message}`);
            }
        }
    };
    

    removeCell.appendChild(removeButton);

    // Add change handlers
    destSelect.addEventListener('change', async () => {
        // If destination is changed to empty value, do nothing
        if (!destSelect.value) return;
        
        // If source has a value, set up or update the mapping
        if (sourceSelect.value) {
            columnMappings.set(destSelect.value, sourceSelect.value);
            try {
                await transformColumn(destSelect.value, sourceSelect.value);
            } catch (error) {
                console.error("Error transforming column:", error);
                // Already handled in transformColumn function
            }
        } else {
            // Clear any existing mapping if source is empty
            columnMappings.delete(destSelect.value);
        }
    });

    sourceSelect.addEventListener('change', async () => {
        // Only proceed if destination has a value
        if (!destSelect.value) return;
        
        // If source is changed to empty value, remove the mapping without transforming
        if (!sourceSelect.value) {
            columnMappings.delete(destSelect.value);
            logMessage(`Removed mapping for ${destSelect.value}`);
            return;
        }
        
        // Otherwise set up or update the mapping and transform
        columnMappings.set(destSelect.value, sourceSelect.value);
        try {
            await transformColumn(destSelect.value, sourceSelect.value);
        } catch (error) {
            console.error("Error transforming column:", error);
            // Already handled in transformColumn function
        }
    });

    // Assemble the row
    row.appendChild(destCell);
    row.appendChild(sourceCell);
    row.appendChild(removeCell);

    return row;
}

// Helper function to convert column index to Excel column letter (0 = A, 1 = B, etc.)
function columnIndexToLetter(index) {
    let letter = '';
    
    while (index >= 0) {
        letter = String.fromCharCode((index % 26) + 65) + letter;
        index = Math.floor(index / 26) - 1;
    }
    
    return letter;
}

function formatValidationType(type) {
    return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
}

// Update this function to show more examples with better labeling
function createInferenceContent(inferenceSummaryData) {
    const inferenceContent = document.createElement('div');
    inferenceContent.className = 'tab-content-census inference-panel';
    
    if (inferenceSummaryData && inferenceSummaryData.status === 'success' && 
        inferenceSummaryData.changes && inferenceSummaryData.changes.length > 0) {
        
        inferenceContent.innerHTML = `
            <div class="inference-summary">
                <p>Found ${inferenceSummaryData.summary.total_changes} changes across ${inferenceSummaryData.summary.affected_columns} columns</p>
            </div>
        `;
        
        // Group inference changes by pattern
        const groupedData = groupInferenceChangesByPattern(inferenceSummaryData.changes);
        
        // Create groups section
        if (Object.keys(groupedData.groups).length > 0) {
            const groupsContainer = document.createElement('div');
            groupsContainer.className = 'inference-groups';
            
            // Create header
            const groupsHeader = document.createElement('h3');
            groupsHeader.textContent = 'Grouped Inferences';
            groupsContainer.appendChild(groupsHeader);
            
            // Create expandable groups
            Object.entries(groupedData.groups).forEach(([groupName, groupData]) => {
                const groupDiv = document.createElement('div');
                groupDiv.className = 'inference-group';
                
                // Create collapsible header
                const header = document.createElement('div');
                header.className = 'group-header';
                header.innerHTML = `
                    <span class="expand-icon">▶</span>
                    <span class="group-title">${formatValidationType(groupData.type)}</span>
                    <span class="group-count">${groupData.count}</span>
                    <span class="group-description">${groupName}</span>
                `;
                
                // Create collapsible content
                const content = document.createElement('div');
                content.className = 'group-content collapsed';
                
                // Create preview section with first 10 items (or all if less than 10)
                const previewItems = groupData.changes.slice(0, 10);
                const hasMoreItems = groupData.changes.length > 10;
                
                const examples = document.createElement('div');
                examples.className = 'examples';
                examples.innerHTML = `
                    <h4>Showing ${previewItems.length}${hasMoreItems ? ' of ' + groupData.count : ''} changes:</h4>
                    <ul>
                        ${previewItems.map(change => `
                            <li data-row="${change.row}" data-column="${change.col}">
                                Row ${change.row}: ${change.description}
                                <div class="change-details">
                                    Old: <span class="old-value">${change.old_value}</span>
                                    → 
                                    New: <span class="new-value">${change.new_value}</span>
                                </div>
                            </li>
                        `).join('')}
                    </ul>
                `;
                
                // Create "View All" button if there are more than 10 examples
                if (hasMoreItems) {
                    const viewAllBtn = document.createElement('button');
                    viewAllBtn.className = 'view-all-btn';
                    viewAllBtn.textContent = `View all ${groupData.count} changes`;
                    viewAllBtn.onclick = () => expandInferenceList(groupData.changes);
                    examples.appendChild(viewAllBtn);
                }
                
                content.appendChild(examples);
                
                // Add to group
                groupDiv.appendChild(header);
                groupDiv.appendChild(content);
                
                // Add toggle behavior
                header.addEventListener('click', () => {
                    content.classList.toggle('collapsed');
                    header.querySelector('.expand-icon').textContent = 
                        content.classList.contains('collapsed') ? '▶' : '▼';
                });
                
                groupsContainer.appendChild(groupDiv);
            });
            
            inferenceContent.appendChild(groupsContainer);
        }
        
        // Individual changes section
        if (groupedData.uniqueChanges.length > 0) {
            const uniqueHeader = document.createElement('h3');
            uniqueHeader.textContent = 'Individual Changes';
            inferenceContent.appendChild(uniqueHeader);
            
            const uniqueList = document.createElement('ul');
            uniqueList.className = 'individual-changes';
            
            groupedData.uniqueChanges.forEach(change => {
                const item = document.createElement('li');
                item.dataset.row = change.row;
                item.dataset.column = change.col;
                item.innerHTML = `
                    Row ${change.row}: ${change.description} (${change.col})
                    <div class="change-details">
                        Old: <span class="old-value">${change.old_value}</span>
                        → 
                        New: <span class="new-value">${change.new_value}</span>
                    </div>
                `;
                item.addEventListener('click', () => highlightCell(change.row, change.col));
                uniqueList.appendChild(item);
            });
            
            inferenceContent.appendChild(uniqueList);
        }
    } else {
        inferenceContent.innerHTML = `
            <div class="inference-success">
                <p>No AI inferences were made</p>
            </div>
        `;
    }
    
    // Add click handlers for inference items
    const inferenceItems = inferenceContent.querySelectorAll('.examples li, .individual-changes li');
    inferenceItems.forEach(item => {
        item.addEventListener('click', () => {
            const row = parseInt(item.dataset.row);
            const column = item.dataset.column;
            highlightCell(row, column);
        });
    });
    
    return inferenceContent;
}

function groupInferenceChangesByPattern(changes) {
    // Group by operation type and field
    const groups = {};
    const uniqueChanges = [];
    
    changes.forEach(change => {
        // Extract field name (column)
        const field = change.col;
        
        // Identify operation type from description
        let operationType = "";
        if (change.description.includes("set to")) {
            operationType = "Value Assignment";
        } else if (change.description.includes("calculated")) {
            operationType = "Calculation";
        } else if (change.description.includes("inferred")) {
            operationType = "AI Inference";
        } else if (change.description.includes("corrected")) {
            operationType = "Correction";
        } else if (change.description.includes("formatted")) {
            operationType = "Formatting";
        } else {
            operationType = "Other Change";
        }
        
        // Create a key that combines the operation and field
        const groupKey = `${operationType}: ${field}`;
        
        if (!groups[groupKey]) {
            groups[groupKey] = {
                type: change.inference_type,
                field: field,
                operationType: operationType,
                count: 1,
                changes: [change],
                examples: [change]
            };
        } else {
            groups[groupKey].count++;
            groups[groupKey].changes.push(change);
            
            // Keep up to 3 examples
            if (groups[groupKey].examples.length < 3) {
                groups[groupKey].examples.push(change);
            }
        }
    });
    
    // Convert to final format
    const groupedChanges = {};
    const significantGroups = [];
    
    // Process groups with 2 or more changes
    Object.entries(groups).forEach(([key, group]) => {
        if (group.count >= 2) {
            significantGroups.push([key, group]);
        } else {
            // Add single changes to unique changes list
            uniqueChanges.push(group.examples[0]);
        }
    });
    
    // Sort by count (descending)
    significantGroups.sort((a, b) => b[1].count - a[1].count);
    
    // Create final grouped changes structure
    significantGroups.forEach(([key, group]) => {
        groupedChanges[key] = {
            type: group.type,
            count: group.count,
            field: group.field,
            examples: group.examples,
            changes: group.changes
        };
    });
    
    return {
        groups: groupedChanges,
        uniqueChanges: uniqueChanges
    };
}


function groupValidationIssuesByPattern(issues) {
    // Group by issue type and column
    const groups = {};
    const uniqueIssues = [];
    
    issues.forEach(issue => {
        // Extract meaningful parts from the message
        const column = issue.column;
        let issueType = "";
        
        if (issue.message.includes("is not a valid")) {
            issueType = "Invalid Value";
        } else if (issue.message.includes("missing")) {
            issueType = "Missing Value";
        } else if (issue.message.includes("ZIP code")) {
            issueType = "ZIP Code Issue";
        } else if (issue.message.includes("state")) {
            issueType = "State Code Issue";
        } else if (issue.message.includes("date")) {
            issueType = "Date Format Issue";
        } else {
            issueType = "Other Issue";
        }
        
        // Create a group key combining issue type and column
        const groupKey = `${issueType}: ${column}`;
        
        if (!groups[groupKey]) {
            groups[groupKey] = {
                type: issue.type,
                column: column,
                issueCategory: issueType,
                count: 1,
                affectedRows: [issue.row],
                examples: [issue]
            };
        } else {
            groups[groupKey].count++;
            groups[groupKey].affectedRows.push(issue.row);
            
            // Keep up to 3 examples
            if (groups[groupKey].examples.length < 10) {
                groups[groupKey].examples.push(issue);
            }
        }
    });
    
    // Convert to final format
    const groupedIssues = {};
    const significantGroups = [];
    
    // Process groups with 2 or more issues
    Object.entries(groups).forEach(([key, group]) => {
        if (group.count >= 2) {
            significantGroups.push([key, group]);
        } else {
            // Add single issues to unique issues list
            uniqueIssues.push(group.examples[0]);
        }
    });
    
    // Sort by count (descending)
    significantGroups.sort((a, b) => b[1].count - a[1].count);
    
    // Create final grouped issues structure
    significantGroups.forEach(([key, group]) => {
        groupedIssues[key] = {
            type: group.type,
            count: group.count,
            column: group.column,
            examples: group.examples,
            affectedRows: group.affectedRows,
            issueCategory: group.issueCategory
        };
    });
    
    return {
        groups: groupedIssues,
        uniqueIssues: uniqueIssues
    };
}

// Function to highlight multiple cells
async function highlightMultipleCells(rows, column) {
    try {
        await Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const columnIndex = await getColumnIndex(column);
            
            // Create a non-contiguous range by union
            let range = null;
            
            for (const row of rows) {
                const cellRange = sheet.getCell(row - 1, columnIndex);
                
                if (!range) {
                    range = cellRange;
                } else {
                    range = range.union(cellRange);
                }
            }
            
            if (range) {
                // Apply highlighting
                range.format.fill.color = "#FFFF00";  // Yellow highlight
                
                // Select the first cell to navigate there
                sheet.getCell(rows[0] - 1, columnIndex).select();
            }
            
            await context.sync();
            
            // Reset highlight after 3 seconds
            setTimeout(() => {
                Excel.run(async (innerContext) => {
                    const innerSheet = innerContext.workbook.worksheets.getActiveWorksheet();
                    const ranges = rows.map(row => innerSheet.getCell(row - 1, columnIndex));
                    
                    ranges.forEach(r => r.format.fill.clear());
                    await innerContext.sync();
                });
            }, 3000);
        });
    } catch (error) {
        console.error(`Error highlighting cells: ${error.message}`);
    }
}

// Function to expand inference list in a modal
function expandInferenceList(changes) {
    // Remove existing modals
    const existingModals = document.querySelectorAll('.issue-modal');
    existingModals.forEach(modal => modal.remove());
    
    // Create modal container
    const modal = document.createElement('div');
    modal.className = 'issue-modal';
    
    // Check if changes is valid and not empty
    if (!changes || !Array.isArray(changes) || changes.length === 0) {
        // Handle empty or invalid changes
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>Changes</h3>
                    <span class="close-modal">&times;</span>
                </div>
                <div class="modal-body">
                    <div class="scroll-container">
                        <p style="padding: 20px; text-align: center;">No changes to display.</p>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="highlight-all-btn">Close</button>
                </div>
            </div>
        `;
    } else {
        // Create modal content with changes
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>All Changes (${changes.length})</h3>
                    <span class="close-modal">&times;</span>
                </div>
                <div class="modal-body">
                    <div class="scroll-container">
                        <ul class="modal-list">
                            ${changes.map(change => `
                                <li data-row="${change.row || ''}" data-column="${change.col || ''}">
                                    Row ${change.row || 'Unknown'}: 
                                    ${change.description || 'No description'} 
                                    ${change.col ? `(${change.col})` : ''}
                                    <div class="change-details">
                                        Old: <span class="old-value">${change.old_value || 'N/A'}</span>
                                        → 
                                        New: <span class="new-value">${change.new_value || 'N/A'}</span>
                                    </div>
                                </li>
                            `).join('')}
                        </ul>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="highlight-all-btn">Close</button>
                </div>
            </div>
        `;
    }
    
    // Append to DOM
    document.body.appendChild(modal);
    
    // Add event listeners
    const closeBtn = modal.querySelector('.close-modal');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => {
            modal.remove();
        });
    }
    
    const highlightBtn = modal.querySelector('.highlight-all-btn');
    if (highlightBtn) {
        highlightBtn.addEventListener('click', () => {
            modal.remove();
        });
    }
    
    // Make items clickable
    const listItems = modal.querySelectorAll('.modal-list li');
    listItems.forEach(item => {
        item.addEventListener('click', () => {
            const row = parseInt(item.dataset.row);
            const column = item.dataset.column;
            if (row && column) {
                highlightCell(row, column);
            }
        });
    });
}

// Update the createValidationPanel function to show 10 items by default
function createValidationPanel(validationResults) {
    const validationContent = document.createElement('div');
    validationContent.className = 'tab-content-census validation-panel';
    
    // Check if we have any validation issues (either missing columns or data issues)
    const hasMissingColumns = validationResults && 
                             validationResults.missing_columns && 
                             Object.keys(validationResults.missing_columns).length > 0;
                             
    const hasIssues = validationResults && 
                     validationResults.issues && 
                     validationResults.issues.length > 0;
    
    // If we have no issues at all, show success message and return early
    if (!hasMissingColumns && !hasIssues) {
        validationContent.innerHTML = `
            <div class="validation-success">
                <p>✓ All validations passed successfully</p>
            </div>
        `;
        return validationContent;
    }
    
    // First handle the missing columns section if it exists
    if (hasMissingColumns) {
        const failurePanel = createValidationErrorPanel(validationResults, validationContent);
        if (failurePanel) {
            failurePanel.style.marginBottom = '20px';
            validationContent.appendChild(failurePanel);
        }
    }
    
    // Process validation issues
    if (hasIssues) {
        // Group the issues on the client side
        const groupedData = groupValidationIssuesByPattern(validationResults.issues);
        
        // Create groups section
        if (Object.keys(groupedData.groups).length > 0) {
            const groupsContainer = document.createElement('div');
            groupsContainer.className = 'validation-groups';
            
            // Create header
            const groupsHeader = document.createElement('h3');
            groupsHeader.textContent = 'Grouped Issues';
            groupsContainer.appendChild(groupsHeader);
            
            // Create expandable groups
            Object.entries(groupedData.groups).forEach(([groupName, groupData], index) => {
                const groupDiv = document.createElement('div');
                groupDiv.className = 'issue-group';
                
                // Create collapsible header
                const header = document.createElement('div');
                header.className = 'group-header';
                header.innerHTML = `
                    <span class="expand-icon">▶</span>
                    <span class="group-title">${formatValidationType(groupData.type)}</span>
                    <span class="group-count">${groupData.count}</span>
                    <span class="group-description">${groupName}</span>
                `;
                
                // Create collapsible content
                const content = document.createElement('div');
                content.className = 'group-content collapsed';
                
                // CHANGE HERE: Use the first 10 examples or all if less than 10 (instead of 3)
                const previewItems = groupData.examples.length >= 10 ? 
                    groupData.examples.slice(0, 10) : 
                    groupData.examples;
                const hasMoreItems = groupData.count > previewItems.length;
                
                // Create preview section
                const examples = document.createElement('div');
                examples.className = 'examples';
                examples.innerHTML = `
                    <h4>Showing ${previewItems.length}${hasMoreItems ? ' of ' + groupData.count : ''} issues:</h4>
                    <ul>
                        ${previewItems.map(issue => `
                            <li data-row="${issue.row}" data-column="${issue.column}">
                                Row ${issue.row}: ${issue.message} (${issue.column})
                            </li>
                        `).join('')}
                    </ul>
                `;
                
                // Create "View All" button if there are more than shown examples
                if (hasMoreItems) {
                    const viewAllBtn = document.createElement('button');
                    viewAllBtn.className = 'view-all-btn';
                    viewAllBtn.textContent = `View all ${groupData.count} issues`;
                    viewAllBtn.onclick = () => expandFullList(groupData.affectedRows, groupData.examples[0].column, groupData.examples);
                    examples.appendChild(viewAllBtn);
                }
                
                content.appendChild(examples);
                
                // Add to group
                groupDiv.appendChild(header);
                groupDiv.appendChild(content);
                
                // Add toggle behavior
                header.addEventListener('click', () => {
                    content.classList.toggle('collapsed');
                    header.querySelector('.expand-icon').textContent = 
                        content.classList.contains('collapsed') ? '▶' : '▼';
                });
                
                groupsContainer.appendChild(groupDiv);
            });
            
            validationContent.appendChild(groupsContainer);
        }
        
        // Individual issues section
        if (groupedData.uniqueIssues.length > 0) {
            const uniqueHeader = document.createElement('h3');
            uniqueHeader.textContent = 'Individual Issues';
            validationContent.appendChild(uniqueHeader);
            
            const uniqueList = document.createElement('ul');
            uniqueList.className = 'individual-issues';
            
            groupedData.uniqueIssues.forEach(issue => {
                const item = document.createElement('li');
                item.dataset.row = issue.row;
                item.dataset.column = issue.column;
                item.innerHTML = `Row ${issue.row}: ${issue.message} (${issue.column})`;
                item.addEventListener('click', () => highlightCell(issue.row, issue.column));
                uniqueList.appendChild(item);
            });
            
            validationContent.appendChild(uniqueList);
        }
    }
    
    // Add click handlers for validation items
    const issueItems = validationContent.querySelectorAll('.validation-issues li, .examples li, .individual-issues li');
    issueItems.forEach(item => {
        item.addEventListener('click', () => {
            const row = parseInt(item.dataset.row);
            const column = item.dataset.column;
            highlightCell(row, column);
        });
    });
    
    return validationContent;
}

// SOLUTION FOR MISSING DESCRIPTIONS IN EXPANDED VIEW
// Modify the expandFullList function to include descriptions
function expandFullList(rows, column, examples) {
    // Remove existing modals
    const existingModals = document.querySelectorAll('.issue-modal');
    existingModals.forEach(modal => modal.remove());
    
    // Create modal container
    const modal = document.createElement('div');
    modal.className = 'issue-modal';
    
    // Create a map of row numbers to their messages from the examples
    const rowMessages = {};
    if (examples && Array.isArray(examples)) {
        examples.forEach(example => {
            if (example.row && example.message) {
                rowMessages[example.row] = example.message;
            }
        });
    }
    
    // Check if rows is valid and not empty
    if (!rows || !Array.isArray(rows) || rows.length === 0) {
        // Handle empty or invalid rows
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>Affected Rows</h3>
                    <span class="close-modal">&times;</span>
                </div>
                <div class="modal-body">
                    <div class="scroll-container">
                        <p style="padding: 20px; text-align: center;">No rows to display.</p>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="highlight-all-btn">Close</button>
                </div>
            </div>
        `;
    } else {
        // Create modal content with rows and include descriptions where available
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>All Affected Rows (${rows.length})</h3>
                    <span class="close-modal">&times;</span>
                </div>
                <div class="modal-body">
                    <div class="scroll-container">
                        <ul class="modal-list">
                            ${rows.map(row => {
                                // Get message for this row if available
                                const message = rowMessages[row] || `Invalid value in ${column}`;
                                return `
                                    <li data-row="${row}" data-column="${column}">
                                        Row ${row}: ${message} (${column})
                                    </li>
                                `;
                            }).join('')}
                        </ul>
                    </div>
                </div>
                <div class="modal-footer">
                    <button class="highlight-all-btn">Close</button>
                </div>
            </div>
        `;
    }
    
    // Append to DOM
    document.body.appendChild(modal);
    
    // Add event listeners
    const closeBtn = modal.querySelector('.close-modal');
    if (closeBtn) {
        closeBtn.addEventListener('click', () => {
            modal.remove();
        });
    }
    
    const highlightBtn = modal.querySelector('.highlight-all-btn');
    if (highlightBtn) {
        highlightBtn.addEventListener('click', () => {
            modal.remove();
        });
    }
    
    // Make items clickable
    const listItems = modal.querySelectorAll('.modal-list li');
    listItems.forEach(item => {
        item.addEventListener('click', () => {
            const row = parseInt(item.dataset.row);
            const column = item.dataset.column;
            if (row && column) {
                highlightCell(row, column);
            }
        });
    });
}

// Find where this error occurs (around line 3711)
// Replace code like this:
document.getElementById("loading-indicator").style.display = "none";

// With this safer version that includes a null check:
const loadingIndicator = document.getElementById("loading-indicator");
if (loadingIndicator) {
    loadingIndicator.style.display = "none";
}

// Do this everywhere you access the loading-indicator element
// For example in the processAsyncCensus function:

/**
 * Process census data asynchronously with polling for completion
 * @param {object} options Configuration options
 * @param {number} options.pollIntervalMs Time between status polls in milliseconds (default: 2000)
 * @param {number} options.timeoutMinutes Maximum timeout in minutes (default: 10)
 * @param {Function} options.onProgress Callback function for progress updates
 * @param {Function} options.onStatusChange Callback function for status changes
 * @returns {Promise<object>} The processing result
 */
async function processAsyncCensus(options = {}) {
    // Record the start time when processing begins
    const startTime = new Date();
    
    try {
        // Clear previous errors
        const productsError = document.getElementById("products-error");
        if (productsError) {
            productsError.style.display = "none";
        }
        
        const censusTypeError = document.getElementById("census-type-error");
        if (censusTypeError) {
            censusTypeError.style.display = "none";
        }
        // Clear main error display
        const errorDisplay = document.getElementById("error-display");
        if (errorDisplay) {
            errorDisplay.style.display = "none";
        }
        // Get selected products
        const productSelect = document.getElementById("products-select");
        if (!productSelect) {
            logMessage("Products select element not found");
            return;
        }
        
        const selectedProducts = Array.from(productSelect.selectedOptions).map(option => option.value);

        // Get selected census type
        const selectedCensusType = document.querySelector('input[name="censusType"]:checked')?.value;

        // Validate selections
        let hasError = false;

        if (selectedProducts.length === 0) {
            const errorElement = document.getElementById("products-error");
            if (errorElement) {
                errorElement.textContent = "Please select at least one product";
                errorElement.style.display = "block";
            }
            hasError = true;
        }

        if (!selectedCensusType) {
            const errorElement = document.getElementById("census-type-error");
            if (errorElement) {
                errorElement.textContent = "Please select a census type";
                errorElement.style.display = "block";
            }
            hasError = true;
        }

        if (hasError) {
           return;
        }

        // Log selections
        logMessage("Selected products: " + selectedProducts.join(", "));
        logMessage("Census type: " + selectedCensusType);

        // Start processing
        logMessage("Starting to process census data asynchronously...");
        
        // Ensure loading indicator is properly initialized
        let loadingIndicator = document.getElementById("loading-indicator");
        
        // Check if we need to reinitialize the loading indicator
        if (!loadingIndicator || loadingIndicator.style.display === "none") {
            // If the indicator doesn't exist or is hidden, find the parent container
            const loadingContainer = document.querySelector('.loading-section');
            
            if (loadingContainer) {
                // Reset the container to make it visible
                loadingContainer.style.display = "block";
                
                // Look for the loading indicator inside the container
                loadingIndicator = loadingContainer.querySelector('#loading-indicator');
                
                // If not found, try to find it again in the document
                if (!loadingIndicator) {
                    loadingIndicator = document.getElementById("loading-indicator");
                }
            } else {
                // Get the button-group container which should be the parent of the loading indicator
                const buttonGroup = document.querySelector('.button-group');
                if (buttonGroup) {
                    // Find any existing loading section
                    let existingLoadingSection = buttonGroup.querySelector('.loading-section');
                    
                    if (!existingLoadingSection) {
                        // Create a new loading section if none exists
                        const tableCell = buttonGroup.querySelector('td:nth-child(2)');
                        
                        if (tableCell) {
                            // Recreate the loading indicator structure
                            existingLoadingSection = document.createElement('div');
                            existingLoadingSection.id = 'loading-indicator';
                            existingLoadingSection.className = 'loading-section';
                            existingLoadingSection.innerHTML = `
                                <table><tr><td><div class="spinner"></div></td>
                                <td><div class="ms-font-m" style="font-size:10px">Processing...</div></td></tr>
                                </table>
                            `;
                            tableCell.appendChild(existingLoadingSection);
                        }
                    }
                    
                    // Make sure it's visible
                    if (existingLoadingSection) {
                        existingLoadingSection.style.display = "block";
                    }
                }
            }
        } else if (loadingIndicator) {
            // Make sure it's visible
            loadingIndicator.style.display = "block";
        }
        
        const checkCensusButton = document.getElementById("check-census");
        if (checkCensusButton) {
            checkCensusButton.disabled = true;
        }
        
        // Set up the timer with "Gathering sheet data" message
        setupWaitingTimer();
        updateWaitingTimerStatus("Gathering sheet data...");
        
        logMessage("Current Mappings...");
        logMessage(Array.from(columnMappings.entries()).map(([key, value]) => `${key}: ${value}`).join(', '));

        // Get the timeout value from settings
        let timeoutMinutes = 10; // Default to 10 minutes
        const timeoutInput = document.getElementById("timeout-minutes");
        if (timeoutInput) {
            const inputValue = parseInt(timeoutInput.value, 10);
            // Validate the input value (must be between 1 and 120 minutes)
            if (!isNaN(inputValue) && inputValue >= 1 && inputValue <= 120) {
                timeoutMinutes = inputValue;
            } else {
                // Log a warning if the input value is invalid
                logMessage(`Invalid timeout value: ${timeoutInput.value}. Using default (10 minutes).`);
            }
        }

        // Configure default options
        const {
            pollIntervalMs = 2000,
            onProgress = (progress) => {
                logMessage(`Processing progress: ${progress}%`);
            },
            onStatusChange = (status, step) => {
                logMessage(`Status changed to: ${status}, step: ${step}`);
            }
        } = options;

        // Convert timeout minutes to max attempts
        // maxAttempts = (timeoutMinutes * 60 * 1000) / pollIntervalMs
        const maxAttempts = Math.ceil((timeoutMinutes * 60 * 1000) / pollIntervalMs);
        logMessage(`Setting timeout to ${timeoutMinutes} minutes (${maxAttempts} polling attempts)`);

        // Collect Excel data with UI feedback
        logMessage("Gathering Excel sheet data...");
        const excelData = await Excel.run(async (context) => {
            // Get workbook name
            const workbook = context.workbook;
            workbook.load("name");
            await context.sync();
            
            logMessage(`Workbook name loaded: ${workbook.name}`);
            
            // Get all worksheets
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");  // Make sure to load sheet names
            await context.sync();

            if (worksheets.items.length === 0) {
                throw new Error("No worksheets found in workbook");
            }

            logMessage(`Loaded ${worksheets.items.length} worksheets.`);

            // Find Census sheet
            const sheet = worksheets.items.find(sheet => sheet.name.toLowerCase() === 'census');
            if (!sheet) {
                const errorDisplay = document.getElementById("error-display");
                errorDisplay.style.display = "block";
                errorDisplay.innerHTML = `<p>Error: Please ensure there is a sheet named 'Census' containing source data</p>`;
                throw new Error("Census sheet not found");
            }

            logMessage(`Selected sheet: ${sheet.name}`);
            const range = sheet.getUsedRange();
     
            // Get settings
            const inferenceType = document.querySelector('input[name="inferenceType"]:checked')?.value || "basic";
            const confidenceScore = inferenceType === "advanced" 
                 ? document.getElementById("confidence-score")?.value 
                 : null;
        
            const enable_ai = inferenceType == "advanced";
            logMessage(`Settings: Inference type - ${inferenceType}, Confidence score - ${confidenceScore}, AI enable status - ${enable_ai}`);
            
            // Prepare the Excel data
            range.load(["values", "rowCount", "columnCount"]);
            await context.sync();

            if (!range.values || range.values.length === 0) {
                throw new Error(`Selected sheet is empty: ${sheet.name}`);
            }
            if (checkDuplicateHeaders(range.values[0])) {
                throw new Error("Duplicate headers found");
            }        

            logMessage(`Sheet ${sheet.name} has data: ${range.rowCount} rows, ${range.columnCount} columns`);
            
            // Get the original headers and clean them to remove text in parentheses
            const originalHeaders = range.values[0];
            const headers = originalHeaders.map(header => cleanHeaderText(header));
            
            const data = [];

            const rowsToProcess = range.values.length;

            updateWaitingTimerStatus(`Processing ${rowsToProcess - 1} rows...`);
            
            for (let i = 1; i < rowsToProcess; i++) {
                const row = {};
                row["_rowNumber"] = i + 1;
                
                let isEmptyRow = true;
                for (let j = 0; j < headers.length; j++) {
                    if (range.values[i][j] !== undefined && range.values[i][j] !== '') {
                        row[headers[j]] = range.values[i][j];
                        isEmptyRow = false;
                    }
                }
                if (!isEmptyRow) {
                    data.push(row);
                }
            }
            
            // Return the collected data
            return {
                workbookName: workbook.name,
                sheetName: sheet.name,
                headers: headers,  // This now contains the cleaned headers
                data: data,
                totalRows: range.rowCount - 1
            };
        });
        
        // Update the timer to show that data collection is complete
        updateWaitingTimerStatus("Data collected, submitting job...");

        // Prepare API request data
        const cacheEnabled = document.getElementById("enable-caching")?.checked;
        const inferenceType = document.querySelector('input[name="inferenceType"]:checked')?.value || "basic";
        const confidenceScore = inferenceType === "advanced" 
             ? document.getElementById("confidence-score")?.value 
             : null;
    
        const enable_ai = inferenceType == "advanced";
        
        let allSheetsData = {
            filename: excelData.workbookName,
            sheets: {},
            products: selectedProducts,
            censusType: selectedCensusType,
            enable_ai: enable_ai,
            confidenceScore: confidenceScore,
            columnMappings: Object.fromEntries(columnMappings),
            cacheEnabled: cacheEnabled
        };
        
        // Add sheet data
        allSheetsData.sheets[excelData.sheetName] = {
            headers: excelData.headers,  // Using the cleaned headers
            data: excelData.data,
            totalRows: excelData.totalRows
        };

        // Submit the job
        updateWaitingTimerStatus("Submitting job...");
        const apiUrl = await ConfigManager.getApiUrl('/process-census-async');
        logMessage(`Calling API for process-census-async: ${apiUrl}`);
        
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            mode: 'cors',
            body: JSON.stringify(allSheetsData)
        });
        
        if (!response.ok) {
            throw new Error(`HTTPS error! status: ${response.status}`);
        }
        
        const jobResponse = await response.json();
        
        if (jobResponse.status === 'pending' && jobResponse.job_id) {
            const jobId = jobResponse.job_id;
            logMessage(`Job submitted with ID: ${jobId}`);
            
            // Update the timer message
            updateWaitingTimerStatus("Processing...");
            
            // Poll for completion
            const result = await pollJobStatus({
                jobId,
                pollIntervalMs,
                maxAttempts,
                timeoutMinutes, // Pass through the minutes for UI display
                onProgress,
                onStatusChange
            });

            // Fetch the complete results - update waiting timer display to "Getting Census Summary"
            logMessage(`Job completed, fetching results...`);
            updateWaitingTimerStatus("Getting Census Summary");
            const fullResult = await fetchJobResult(jobId);
            logMessage(`Fetched results...`);

            // Calculate processing duration
            const endTime = new Date();
            const processingDuration = (endTime - startTime) / 1000; // in seconds
            
            // Add duration to the result
            fullResult.processingDuration = processingDuration;

            // Process the result
            logMessage(`Job completed, calling processJobResult...`);
            processJobResult(fullResult);
            return fullResult;
        } else {
            throw new Error('Invalid API response: Missing job ID');
        }
        
    } catch (error) {
        logMessage("ProcessAsyncCensus error:", error.toString());
        console.error("ProcessAsyncCensus error:", error);
        const errorDisplay = document.getElementById("error-display");
        if (errorDisplay) {
            errorDisplay.style.display = "block";
            errorDisplay.innerHTML = `<p>Error: ${error.message}</p>`;
        }
    } finally {
        logMessage("Processing complete.");
        
        // Just hide the loading indicator, don't remove it completely
        const loadingIndicator = document.getElementById("loading-indicator");
        if (loadingIndicator) {
            loadingIndicator.style.display = "none";
        } else {
            // Try to find it in the loading-section
            const loadingSection = document.querySelector('.loading-section');
            if (loadingSection) {
                loadingSection.style.display = "none";
            }
        }
        
        const checkCensusButton = document.getElementById("check-census");
        if (checkCensusButton) {
            checkCensusButton.disabled = false;
        }
    }
}

/**
 * Submit the census job to the API
 * @param {object} jobData Job parameters including products, censusType, etc.
 * @returns {Promise<string>} The job ID
 */
async function submitCensusJob(jobData) {
    let jobId = null;
    
    try {
        // First get all the Excel data
        const excelData = await Excel.run(async (context) => {
            // Get workbook name
            const workbook = context.workbook;
            workbook.load("name");
            await context.sync();
            
            logMessage(`Workbook name loaded: ${workbook.name}`);
            
            // Get all worksheets
            const worksheets = context.workbook.worksheets;
            worksheets.load("items/name");  // Make sure to load sheet names
            await context.sync();

            if (worksheets.items.length === 0) {
                throw new Error("No worksheets found in workbook");
            }

            logMessage(`Loaded ${worksheets.items.length} worksheets.`);

            // Find Census sheet
            const sheet = worksheets.items.find(sheet => sheet.name === 'Census');
            if (!sheet) {
                const errorDisplay = document.getElementById("error-display");
                errorDisplay.style.display = "block";
                errorDisplay.innerHTML = `<p>Error: Please ensure there is a sheet named 'Census' containing source data</p>`;
                throw new Error("Census sheet not found");
            }

            logMessage(`Selected sheet: ${sheet.name}`);
            const range = sheet.getUsedRange();
     
            // Get settings
            const inferenceType = document.querySelector('input[name="inferenceType"]:checked')?.value || "basic";
            const confidenceScore = inferenceType === "advanced" 
                 ? document.getElementById("confidence-score")?.value 
                 : null;
        
            const enable_ai = inferenceType == "advanced";
            logMessage(`Settings: Inference type - ${inferenceType}, Confidence score - ${confidenceScore}, AI enable status - ${enable_ai}`);
            
            // Prepare the Excel data
            range.load(["values", "rowCount", "columnCount"]);
            await context.sync();

            if (!range.values || range.values.length === 0) {
                throw new Error(`Selected sheet is empty: ${sheet.name}`);
            }
            if (checkDuplicateHeaders(range.values[0])) {
                throw new Error("Duplicate headers found");
            }        

            logMessage(`Sheet ${sheet.name} has data.`);
            
            // Convert to JSON
            const originalHeaders = range.values[0];
            // Clean the headers by removing text in parentheses
            const headers = originalHeaders.map(header => cleanHeaderText(header));
            const data = [];

            const rowsToProcess = range.values.length;

            for (let i = 1; i < rowsToProcess; i++) {
                const row = {};
                row["_rowNumber"] = i + 1;
                
                let isEmptyRow = true;
                for (let j = 0; j < headers.length; j++) {
                    if (range.values[i][j] !== undefined && range.values[i][j] !== '') {
                        row[headers[j]] = range.values[i][j];
                        isEmptyRow = false;
                    }
                }
                if (!isEmptyRow) {
                    data.push(row);
                }
            }
            
            // Return the collected data
            return {
                workbookName: workbook.name,
                sheetName: sheet.name,
                headers: headers,  // Now using cleaned headers
                data: data,
                totalRows: range.rowCount - 1
            };
        });
        
        // Prepare API request data
        const cacheEnabled = document.getElementById("enable-caching")?.checked;
        const inferenceType = document.querySelector('input[name="inferenceType"]:checked')?.value || "basic";
        const confidenceScore = inferenceType === "advanced" 
             ? document.getElementById("confidence-score")?.value 
             : null;
    
        const enable_ai = inferenceType == "advanced";
        
        let allSheetsData = {
            filename: excelData.workbookName,
            sheets: {},
            products: jobData.products,
            censusType: jobData.censusType,
            enable_ai: enable_ai,
            confidenceScore: confidenceScore,
            columnMappings: jobData.columnMappings,
            cacheEnabled: cacheEnabled
        };
        
        // Add sheet data with cleaned headers
        allSheetsData.sheets[excelData.sheetName] = {
            headers: excelData.headers,
            data: excelData.data,
            totalRows: excelData.totalRows
        };
        
        logMessage(`Processed sheet ${excelData.sheetName}:`, {
            headerCount: excelData.headers.length,
            rowCount: excelData.data.length,
            totalRowsInSheet: excelData.totalRows,
            sampleRow: excelData.data[0]
        });
        
        // Clear any old error displays
        const errorDisplay = document.getElementById("error-display");
        errorDisplay.style.display = "none";
        const actionsPanel = document.getElementById('actions-panel');
        actionsPanel.style.display = "none";

        // Call the async API endpoint
        logMessage("Calling async API:", allSheetsData.filename);
        
        const apiUrl = await ConfigManager.getApiUrl('/process-census-async');
        logMessage("Calling API for process-census-async: " + apiUrl);
        
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            mode: 'cors',
            body: JSON.stringify(allSheetsData)
        });
              
        if (!response.ok) {
            throw new Error(`HTTPS error! status: ${response.status}`);
        }

        const result = await response.json();
        
        if (result.status === 'pending' && result.job_id) {
            logMessage(`Async job created with ID: ${result.job_id}`);
            jobId = result.job_id;
        } else {
            throw new Error('Invalid API response: Missing job ID');
        }
        
        return jobId;
    } catch (error) {
        logMessage(`Error submitting census job: ${error.message}`);
        throw error;
    }
}

// Timer management improvements with timeout display removed

// Store timer globally for easier access and cleanup
let globalTimerInterval = null;
let globalWaitingSeconds = 0;

/**
 * Create or update the waiting timer element
 * Ensures proper cleanup of any existing timers first
 */
function setupWaitingTimer() {
    // First, make sure any existing timer is stopped
    clearWaitingTimer();
    
    const loadingIndicator = document.getElementById("loading-indicator");
    if (!loadingIndicator) return;
    
    // Remove any existing timer elements to avoid duplicates
    const existingTimer = document.getElementById("waiting-timer");
    if (existingTimer) {
        existingTimer.remove();
    }
    
    // Create fresh timer element (without timeout display)
    const waitingTimer = document.createElement("div");
    waitingTimer.id = "waiting-timer";
    waitingTimer.style.marginTop = "5px";
    waitingTimer.style.fontSize = "14px";
    waitingTimer.style.color = "#666";
    loadingIndicator.appendChild(waitingTimer);
    
    // Reset and start the timer
    globalWaitingSeconds = 0;
    updateTimerDisplay();
    
    // Start a new timer interval
    globalTimerInterval = setInterval(() => {
        globalWaitingSeconds++;
        updateTimerDisplay();
    }, 1000);
}

/**
 * Updates the timer display with current waiting time
 */
function updateTimerDisplay() {
    const waitingTimer = document.getElementById("waiting-timer");
    if (waitingTimer) {
        const minutes = Math.floor(globalWaitingSeconds / 60);
        const seconds = globalWaitingSeconds % 60;
        waitingTimer.textContent = `Processing time: ${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
}

/**
 * Updates the waiting timer text to show a status message
 * @param {string} statusText - The status text to display
 */
function updateWaitingTimerStatus(statusText) {
    const waitingTimer = document.getElementById("waiting-timer");
    if (waitingTimer) {
        waitingTimer.textContent = statusText || "Processing...";
    }
}

/**
 * Clears the waiting timer and interval
 * Removes timer elements if clearElements is true
 * @param {boolean} clearElements - Whether to remove the timer elements from DOM
 */
function clearWaitingTimer(clearElements = false) {
    // Stop the interval
    if (globalTimerInterval) {
        clearInterval(globalTimerInterval);
        globalTimerInterval = null;
    }
    
    // Optionally remove the timer elements
    if (clearElements) {
        const waitingTimer = document.getElementById("waiting-timer");
        if (waitingTimer) {
            waitingTimer.remove();
        }
    }
}

/**
 * Poll the job status until completion or timeout
 * @param {object} options Polling options
 * @param {string} options.jobId The job ID to poll
 * @param {number} options.pollIntervalMs Time between polls in milliseconds
 * @param {number} options.maxAttempts Maximum number of polling attempts
 * @param {number} options.timeoutMinutes Timeout in minutes (for UI display)
 * @param {Function} options.onProgress Callback for progress updates
 * @param {Function} options.onStatusChange Callback for status changes
 * @returns {Promise<object>} The job status when complete
 */
async function pollJobStatus({ jobId, pollIntervalMs, maxAttempts, timeoutMinutes, onProgress, onStatusChange }) {
    let attempts = 0;
    let lastProgress = 0;
    let lastStatus = '';
    const MAX_CONSECUTIVE_ERRORS = 5;
    let consecutiveErrors = 0;
    let serverErrors = 0;
    const MAX_SERVER_ERRORS = 3; // Exit after 3 server errors (5xx)
    
    // Set up waiting timer with fresh elements
    setupWaitingTimer();
    updateWaitingTimerStatus("Processing...");
    
    return new Promise((resolve, reject) => {
        const poll = async () => {
            if (attempts >= maxAttempts) {
                clearWaitingTimer(true); // Clear and remove elements
                reject(new Error(`Job timed out after ${timeoutMinutes} minutes (${Math.floor(globalWaitingSeconds / 60)}:${(globalWaitingSeconds % 60).toString().padStart(2, '0')})`));
                return;
            }
            
            attempts++;
            
            try {
                const apiUrl = await ConfigManager.getApiUrl(`/api/job/${jobId}/status`);
                const response = await fetch(apiUrl, { 
                    signal: AbortSignal.timeout(10000) // 10 second timeout
                });
                
                // Check for server errors (5xx status codes)
                if (response.status >= 500) {
                    serverErrors++;
                    
                    if (serverErrors >= MAX_SERVER_ERRORS) {
                        clearWaitingTimer(true);
                        reject(new Error(`Server error detected: ${response.status}. Processing canceled.`));
                        return;
                    }
                    
                    throw new Error(`Server error: ${response.status}`);
                }
                
                // Reset error counters on successful fetch
                consecutiveErrors = 0;
                serverErrors = 0;
                
                if (!response.ok) {
                    throw new Error(`Error checking job status: ${response.status}`);
                }
                
                const status = await response.json();
                
                // Report progress if changed
                if (status.progress !== lastProgress) {
                    lastProgress = status.progress;
                    onProgress(status.progress);
                }
                
                // Report status change if changed
                if (status.status !== lastStatus) {
                    lastStatus = status.status;
                    onStatusChange(status.status, status.step);
                }
                
                // Update the timeout display with current timeout minutes
                updateTimerDisplay(timeoutMinutes);
                
                // Check job status
                if (status.status === 'complete') {
                    // Add the timer value to the status object
                    status.processingTimeSeconds = globalWaitingSeconds;
                    
                    // Update status but don't clear timer yet
                    updateWaitingTimerStatus("Getting Census Summary");
                    
                    resolve(status);
                    return;
                } else if (status.status === 'error') {
                    clearWaitingTimer(true); // Clear and remove elements
                    reject(new Error(status.error || 'Job failed with unknown error'));
                    return;
                }
                
                // Continue polling
                setTimeout(poll, pollIntervalMs);
                
            } catch (error) {
                consecutiveErrors++;
                
                // Create a more user-friendly error message
                let errorMessage = `Error during status poll: ${error.message}`;
                
                // Check if it's a network-related error
                if (error.name === 'AbortError' || 
                    error.message.includes('NetworkError') || 
                    error.message.includes('Failed to fetch') || 
                    error.message.includes('Load failed')) {
                    
                    errorMessage = `Server connection issue: ${error.message}. Retrying...`;
                    
                    // Update the UI with connection status
                    const loadingIndicator = document.getElementById("loading-indicator");
                    if (loadingIndicator) {
                        // Add connection warning if not already present
                        if (!document.getElementById("connection-warning")) {
                            const warningDiv = document.createElement("div");
                            warningDiv.id = "connection-warning";
                            warningDiv.style.color = "#ff9900";
                            warningDiv.style.marginTop = "5px";
                            warningDiv.textContent = "Server connection issues. Retrying...";
                            loadingIndicator.appendChild(warningDiv);
                        }
                    }
                }
                
                logMessage(errorMessage);
                
                // If too many consecutive errors, increase delay or consider failing
                if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
                    clearWaitingTimer(true);
                    reject(new Error(`Failed after ${MAX_CONSECUTIVE_ERRORS} consecutive errors: ${error.message}`));
                    return;
                }
                
                // Continue polling with normal interval
                setTimeout(poll, pollIntervalMs);
            }
        };
        
        // Start polling
        poll();
    });
}

/**
 * Fetch the complete job result
 * @param {string} jobId The job ID
 * @returns {Promise<object>} The complete job result
 */
async function fetchJobResult(jobId) {
    try {
        const apiUrl = await ConfigManager.getApiUrl(`/api/job/${jobId}/result`);
        logMessage(`Fetching job result from: ${apiUrl}`);
        
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
            throw new Error(`Error fetching job result: ${response.status}`);
        }
        
        // Clear the waiting timer after getting results
        clearWaitingTimer();
        
        return await response.json();
    } catch (error) {
        logMessage(`Error fetching job result: ${error.message}`);
        throw error;
    }
}



/**
 * Process and display the job result in the UI
 * @param {object} result The job result to process
 */
function processJobResult(result) {
    try {
        logMessage("API Response received");
        
        if (result.status === 'success' && result.change_summary !== null) {
            const actionsPanel = document.getElementById('actions-panel');
            actionsPanel.innerHTML = '';
            actionsPanel.appendChild(createResponsePanel(result));
            actionsPanel.style.display = 'block';
            
            // Handle sheet download if applicable
            const hasValidData = result.change_summary["Number of columns mapped"] > 0 || 
                               result.change_summary["Number of values mapped"] > 0;
            
            if (hasValidData && result.sheets_summary) {
                // Check for sheet name based on the convention in your code
                const sheetNameKey = Object.keys(result.sheets_summary).find(key => 
                    key.startsWith('sheet_name_')
                );
                
                if (sheetNameKey) {
                    const sheetName = result.sheets_summary[sheetNameKey];
                    const textFormatColumns = ["Employee ID Number", "Residential Zip Code", "Employee Work Location Code"];
                    handleSheetDownload(sheetName, 'current', textFormatColumns);
                }
            }
        } else if (result.status === 'success' && result.change_summary == null) {
            document.getElementById("check-census").disabled = false;
            
            // Show message that no changes were needed
            const actionsPanel = document.getElementById('actions-panel');
            actionsPanel.innerHTML = `
                <div class="response-panel">
                    <h2 class="title">Census Processing Complete</h2>
                    <div class="no-changes">No changes were required for this census data.</div>
                </div>
            `;
            actionsPanel.style.display = 'block';
            
        } else if (result.validation_results && result.validation_results.status === 'error') {
            // Handle validation errors
            const actionsPanel = document.getElementById('actions-panel');
            actionsPanel.innerHTML = '';
            actionsPanel.appendChild(createResponsePanel(result));
            actionsPanel.style.display = 'block';
            
            // Ensure the validation tab is shown by default
            setTimeout(() => {
                const validationTab = actionsPanel.querySelector('.tab-button:nth-child(1)');
                if (validationTab) {
                    validationTab.click();
                }
            }, 0);
            
        } else {
            throw new Error('Invalid API response structure');
        }
    } catch (error) {
        logMessage(`Error processing job result: ${error.message}`);
        const errorDisplay = document.getElementById("error-display");
        errorDisplay.style.display = "block";
        errorDisplay.innerHTML = `<p>Error: ${error.message}</p>`;
    }
}

/**
 * Cleans column headers by removing brackets and their contents
 * E.g., "Gender (M or F)" becomes "Gender"
 * 
 * @param {string[] | object[]} headers - Array of header strings or objects
 * @returns {string[]} - Cleaned headers
 */
function cleanHeaders(headers) {
    if (!headers || !Array.isArray(headers) || headers.length === 0) {
        return headers;
    }
    
    // Create a new array with cleaned headers
    return headers.map(header => {
        if (typeof header !== 'string') {
            return header; // Keep non-string values as-is
        }
        
        // Remove text in brackets along with the brackets
        // This handles both round brackets () and square brackets []
        return header
            .replace(/\s*\([^)]*\)\s*/g, '') // Remove anything in round brackets with surrounding whitespace
            .replace(/\s*\[[^\]]*\]\s*/g, '') // Remove anything in square brackets with surrounding whitespace
            .trim(); // Trim any remaining whitespace
    });
}

/**
 * Creates a clean version of the data structure with cleaned headers
 * This preserves the original data structure and doesn't mutate the original object
 * 
 * @param {object} data - Object containing sheet data with headers
 * @returns {object} - New object with cleaned headers
 */
function createCleanHeadersData(data) {
    if (!data || typeof data !== 'object') {
        return data;
    }
    
    // Create a deep copy of the data
    const cleanedData = JSON.parse(JSON.stringify(data));
    
    // Clean headers in the sheets object
    if (cleanedData.sheets) {
        Object.keys(cleanedData.sheets).forEach(sheetName => {
            const sheet = cleanedData.sheets[sheetName];
            
            // Clean the headers array if it exists
            if (sheet && sheet.headers && Array.isArray(sheet.headers)) {
                sheet.headers = cleanHeaders(sheet.headers);
                
                // Also clean the keys in each data row
                if (sheet.data && Array.isArray(sheet.data)) {
                    sheet.data = sheet.data.map(row => {
                        if (!row || typeof row !== 'object') {
                            return row;
                        }
                        
                        // Create a new row with cleaned keys
                        const cleanedRow = {};
                        Object.keys(row).forEach(key => {
                            // Special handling for _rowNumber and other system fields
                            if (key === '_rowNumber' || key.startsWith('_')) {
                                cleanedRow[key] = row[key];
                            } else {
                                // Clean the key and use that as the new key
                                const cleanedKey = key.replace(/\s*\([^)]*\)\s*/g, '').replace(/\s*\[[^\]]*\]\s*/g, '').trim();
                                cleanedRow[cleanedKey] = row[key];
                            }
                        });
                        
                        return cleanedRow;
                    });
                }
            }
        });
    }
    
    return cleanedData;
}

/**
 * Cleans a header string by removing text in brackets
 * This function handles round brackets, square brackets, and other common patterns
 * Example: "Gender (M or F)" becomes "Gender"
 * 
 * @param {string} header - The header text to clean
 * @returns {string} - Cleaned header
 */
function cleanHeaderText(header) {
    // Return as-is if not a string
    if (typeof header !== 'string') {
        return header;
    }
    
    // Remove round brackets and their contents
    let cleanedHeader = header.replace(/\s*\([^)]*\)\s*/g, ' ');
    
    // Remove square brackets and their contents
    cleanedHeader = cleanedHeader.replace(/\s*\[[^\]]*\]\s*/g, ' ');
    
    // Remove curly braces and their contents (less common but possible)
    cleanedHeader = cleanedHeader.replace(/\s*\{[^}]*\}\s*/g, ' ');
    
    // Remove angle brackets and their contents (less common but possible)
    cleanedHeader = cleanedHeader.replace(/\s*<[^>]*>\s*/g, ' ');
    
    // Handle any other whitespace issues
    cleanedHeader = cleanedHeader.replace(/\s+/g, ' ').trim();
    
    return cleanedHeader;
}

/**
 * Cleans the header data for API submission
 * This is a non-mutating function that returns a new copy with cleaned headers
 * 
 * @param {object} sheetsData - The Excel sheets data object
 * @returns {object} - A new object with cleaned headers
 */
function cleanHeadersData(sheetsData) {
    // Create a deep copy of the input data
    const cleanedData = JSON.parse(JSON.stringify(sheetsData));
    
    // Process each sheet
    if (cleanedData && cleanedData.sheets) {
        Object.keys(cleanedData.sheets).forEach(sheetName => {
            const sheet = cleanedData.sheets[sheetName];
            
            // Clean the headers array
            if (sheet && Array.isArray(sheet.headers)) {
                sheet.headers = sheet.headers.map(header => cleanHeaderText(header));
                
                // Also clean data row keys to match new headers
                if (Array.isArray(sheet.data)) {
                    // Create a mapping from original to cleaned headers
                    const originalHeaders = sheetsData.sheets[sheetName].headers;
                    const headerMap = {};
                    
                    // Only create mappings where cleaning actually changes the header
                    for (let i = 0; i < originalHeaders.length; i++) {
                        const originalHeader = originalHeaders[i];
                        const cleanedHeader = cleanHeaderText(originalHeader);
                        
                        if (originalHeader !== cleanedHeader) {
                            headerMap[originalHeader] = cleanedHeader;
                        }
                    }
                    
                    // Update all data rows with the clean keys
                    sheet.data = sheet.data.map(row => {
                        const newRow = {...row}; // Create a shallow copy
                        
                        // Update keys that need cleaning
                        Object.keys(headerMap).forEach(oldKey => {
                            if (oldKey in newRow) {
                                const newKey = headerMap[oldKey];
                                newRow[newKey] = newRow[oldKey];
                                delete newRow[oldKey];
                            }
                        });
                        
                        return newRow;
                    });
                }
            }
        });
    }
    
    return cleanedData;
}

/**
 * Function to log column header cleaning
 * This helps with debugging by showing what headers were transformed
 * 
 * @param {Array} originalHeaders - Original column headers
 * @param {Array} cleanedHeaders - Cleaned column headers
 */
function logHeaderCleaning(originalHeaders, cleanedHeaders) {
    if (!originalHeaders || !cleanedHeaders || originalHeaders.length !== cleanedHeaders.length) {
        return;
    }
    
    const changedHeaders = [];
    
    for (let i = 0; i < originalHeaders.length; i++) {
        if (originalHeaders[i] !== cleanedHeaders[i]) {
            changedHeaders.push({
                original: originalHeaders[i],
                cleaned: cleanedHeaders[i]
            });
        }
    }
    
    if (changedHeaders.length > 0) {
        logMessage(`Cleaned ${changedHeaders.length} headers:`);
        changedHeaders.forEach(change => {
            logMessage(`  "${change.original}" → "${change.cleaned}"`);
        });
    } else {
        logMessage("No headers needed cleaning");
    }
}